import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner, Form, Row, Col, Card } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { format, isValid } from 'date-fns';
import debounce from 'lodash/debounce';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, useReducedMotion } from 'framer-motion';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import axiosInstance from '../../api/axios';
import MultiSelectComponent from '../../components/MultiSelectComponent';

/* ------------------------------ API ------------------------------ */
// Update endpoint — adjust here if your backend differs
const updateCouponApi = (body, headers) => axiosInstance.put('coupons', body, { headers });

/* ------------------------------ helpers ------------------------------ */
const toastOpts = { position: 'top-right', autoClose: 3000, theme: 'light' };
const notify = {
    success: (m) => toast.success(m, toastOpts),
    error: (m) => toast.error(m, toastOpts),
};

const getErr = (e, fallback) => {
    const d = e?.response?.data;
    const err = d?.error;
    return (typeof err === 'string' ? err : err?.message) || d?.data?.message || d?.data?.title || fallback;
};

const getSubmitErr = (error, fallback) => {
    const r = error?.response;
    const d = r?.data;
    let msg;
    if (r?.status === 406) msg = d?.error?.message || d?.error || d?.data?.title;
    else if (r?.status === 412 || r?.status === 404) msg = d?.data?.title;
    else if (r?.status === 500) msg = d?.error;
    return (typeof msg === 'string' && msg) || getErr(error, fallback);
};

// Date-only parsing (avoids timezone shifts on ISO strings like 2026-10-03T23:59:59.999Z)
const dateOnly = (v) => {
    const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

const fmtDate = (v) => {
    if (!v) return null;
    const d = typeof v === 'string' ? dateOnly(v) : new Date(v);
    return isValid(d) ? format(d, 'dd MMM yyyy') : null;
};

const fmtDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isValid(d) ? format(d, 'dd MMM yyyy, hh:mm a') : null;
};

const isCouponActive = (c) => {
    if (!c?.validityEndDate) return false;
    const end = String(c.validityEndDate).slice(0, 10);
    const dateOk = end >= format(new Date(), 'yyyy-MM-dd');
    return dateOk && (c.status ? c.status === 'ACTIVE' : true);
};

const GRADIENTS = [
    'linear-gradient(135deg,#6366f1,#8b5cf6)',
    'linear-gradient(135deg,#0ea5e9,#2563eb)',
    'linear-gradient(135deg,#10b981,#059669)',
    'linear-gradient(135deg,#f59e0b,#ef4444)',
    'linear-gradient(135deg,#ec4899,#be185d)',
    'linear-gradient(135deg,#14b8a6,#0e7490)',
];
const gradientFor = (s = '') => GRADIENTS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % GRADIENTS.length];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayOfWeekOptions = DAYS.map((label, value) => ({ value, label }));

const labelOf = (x) => {
    if (x === null || x === undefined) return '';
    if (typeof x === 'string' || typeof x === 'number') return String(x);
    if (x.sequenceNo !== undefined && x.title) return `${x.sequenceNo}. ${x.title}`;
    if (x.addonTitle || x.addonValueTitle) return [x.addonTitle, x.addonValueTitle].filter(Boolean).join(' - ');
    return x.name || x.title || x.label || x.identifier || x._id || '';
};
const labels = (arr) => (Array.isArray(arr) ? arr.map(labelOf).filter(Boolean) : []);

const nz = (v) => (v === null || v === undefined ? '' : v);
const idOf = (x) => (x && typeof x === 'object' ? x._id : x);
const toOpts = (arr) =>
    Array.isArray(arr) ? arr.map((x) => ({ value: idOf(x), label: labelOf(x) || String(idOf(x)) })) : [];

const normalizeUser = (item) => {
    const u = item?.userDetails || item?.user || item || {};
    return {
        id: u._id || item?._id,
        name: u.name || 'N/A',
        mobile: u.mobile || '—',
        email: u.email || '—',
        gender: u.gender || '—',
        dob: u.dob ? fmtDate(u.dob) || '—' : '—',
    };
};

const initials = (name = '') =>
    name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'U';

/* --------------------------- shared constants (user selection) --------------------------- */
const INITIAL_USER_FILTERS = {
    startDate: null,
    endDate: null,
    minPrice: '',
    maxPrice: '',
    outlet: '',
    foodType: '',
    filter: '',
    category: '',
    product: '',
    search: '',
    sortType: '',
    sortBy: 'ASC',
};

const USER_FILTER_SPECIAL_TYPES = ['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'];

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        width: '100%',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#2684ff' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(38,132,255,.25)' : null,
        '&:hover': { borderColor: state.isFocused ? '#2684ff' : '#ced4da' },
        minHeight: '38px',
    }),
    placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const selectTheme = (theme) => ({
    ...theme,
    borderRadius: 6,
    colors: { ...theme.colors, primary25: '#e7f1ff', primary: '#2684ff' },
});

const animatedComponents = makeAnimated();

/* --------------------------- small components --------------------------- */
const CouponCard = ({ coupon, onOpen, index = 0 }) => {
    const [copied, setCopied] = useState(false);
    const [entered, setEntered] = useState(false);
    const reduce = useReducedMotion();

    // 3D tilt + cursor-following glare
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const spring = { stiffness: 180, damping: 18, mass: 0.6 };
    const strength = String(coupon.status || '').toUpperCase() === 'ACTIVE' ? 9 : 3; // inactive cards barely react
    const rotateX = useSpring(useTransform(my, [0, 1], [strength, -strength]), spring);
    const rotateY = useSpring(useTransform(mx, [0, 1], [-strength, strength]), spring);
    const gx = useTransform(mx, (v) => v * 100);
    const gy = useTransform(my, (v) => v * 100);
    const glare = useMotionTemplate`radial-gradient(180px circle at ${gx}% ${gy}%, rgba(255,255,255,.65), rgba(255,255,255,0) 65%)`;

    const onMove = (e) => {
        if (reduce) return;
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => { mx.set(0.5); my.set(0.5); };

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(coupon.couponCode || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            notify.error('Copy failed');
        }
    };

    const isActive = String(coupon.status || '').toUpperCase() === 'ACTIVE';

    return (
        <motion.div
            className={`uca-card ${isActive ? 'is-active' : 'is-inactive'}`}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(coupon)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(coupon)}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            initial={{ opacity: 0, y: 32, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            whileHover="hover"
            whileTap={{ scale: 0.97 }}
            variants={{ hover: { y: -6, transition: { type: 'spring', stiffness: 300, damping: 20 } } }}
            transition={{ type: 'spring', stiffness: 240, damping: 22, delay: entered ? 0 : Math.min(index, 14) * 0.045 }}
            onAnimationComplete={() => setEntered(true)}
            style={{
                '--uca-accent': isActive ? gradientFor(coupon._id) : 'linear-gradient(135deg,#94a3b8,#64748b)',
                rotateX: reduce ? 0 : rotateX,
                rotateY: reduce ? 0 : rotateY,
                transformPerspective: 900,
            }}
        >
            {/* cursor glare */}
            {!reduce && <motion.span className="uca-glare" style={{ background: glare }} />}

            {/* shimmer sweep — only live coupons get it */}
            {isActive && !reduce && (
                <motion.span
                    className="uca-shimmer"
                    initial={{ x: '-140%' }}
                    animate={{ x: '320%' }}
                    transition={{ duration: 1.6, ease: 'easeInOut', repeat: Infinity, repeatDelay: 4 + (index % 5) * 0.7, delay: 0.8 + index * 0.05 }}
                />
            )}

            {/* rubber-stamp for inactive coupons */}
            {!isActive && (
                <motion.span
                    className="uca-stamp"
                    initial={reduce ? false : { scale: 2.4, opacity: 0, rotate: -2 }}
                    animate={{ scale: 1, opacity: 0.6, rotate: -12 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 16, delay: 0.35 + Math.min(index, 14) * 0.045 }}
                >
                    Inactive
                </motion.span>
            )}

            <div className="uca-card-top">
                <motion.div
                    className="uca-avatar"
                    whileHover={reduce ? undefined : { rotate: [0, -8, 8, 0], scale: 1.08 }}
                    transition={{ duration: 0.5 }}
                >
                    {(coupon.title || 'C')[0].toUpperCase()}
                </motion.div>
                <div className="d-flex align-items-center gap-2">
                    <span className={`uca-status ${isActive ? 'active' : 'inactive'}`}>
                        <span className="uca-status-dot"></span>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                    <motion.button
                        type="button"
                        className="uca-copy"
                        onClick={handleCopy}
                        title="Copy code"
                        whileHover={{ scale: 1.12 }}
                        whileTap={{ scale: 0.8 }}
                    >
                        <motion.i
                            key={copied ? 'ok' : 'copy'}
                            className={copied ? 'ri-check-line' : 'ri-file-copy-line'}
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                            style={{ display: 'inline-block', color: copied ? '#16a34a' : undefined }}
                        />
                    </motion.button>
                </div>
            </div>
            <h6 className="uca-card-title" title={coupon.title}>{coupon.title || 'Untitled'}</h6>
            <div className="uca-code">
                <i className={isActive ? 'ri-coupon-3-line' : 'ri-forbid-2-line'}></i>
                <span>{coupon.couponCode || 'N/A'}</span>
            </div>
            <div className="uca-card-foot">
                <span>View allocation</span>
                <motion.i
                    className="ri-arrow-right-line"
                    style={{ display: 'inline-block' }}
                    variants={{ hover: { x: [0, 7, 3, 7], transition: { duration: 0.6 } } }}
                />
            </div>
        </motion.div>
    );
};

const SkeletonCard = () => (
    <div className="uca-card uca-skeleton">
        <div className="uca-sk uca-sk-avatar"></div>
        <div className="uca-sk uca-sk-line w-75"></div>
        <div className="uca-sk uca-sk-line w-50"></div>
    </div>
);

const InfoItem = ({ icon, label, value, children }) => (
    <div className="ucm-item">
        <i className={`${icon} ucm-item-icon`}></i>
        <div className="ucm-item-body">
            <span className="ucm-label">{label}</span>
            <span className="ucm-value">{children ?? value}</span>
        </div>
    </div>
);

const InfoCard = ({ icon, title, className = '', children }) => (
    <section className={`ucm-card ${className}`}>
        <h6 className="ucm-card-title">
            <span className="ucm-card-icon"><i className={icon}></i></span>
            {title}
        </h6>
        {children}
    </section>
);

const Dot = ({ tone = 'green' }) => <span className={`ucm-dot ${tone}`}></span>;

const Pager = ({ page, totalPages, totalRecords, limit, onPage, onLimit }) => {
    const pages = useMemo(() => {
        const start = Math.max(1, Math.min(page - 2, totalPages - 4));
        const end = Math.min(totalPages, start + 4);
        const out = [];
        for (let i = start; i <= end; i++) out.push(i);
        return out;
    }, [page, totalPages]);

    const from = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, totalRecords);

    return (
        <div className="uca-pager">
            <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Rows</small>
                <Form.Select size="sm" style={{ width: 76 }} value={limit} onChange={(e) => onLimit(Number(e.target.value))}>
                    {[10, 20, 50, 100, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
                </Form.Select>
                <small className="text-muted">{from}–{to} of {totalRecords}</small>
            </div>
            <div className="btn-group btn-group-sm">
                <Button variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
                    <i className="ri-arrow-left-s-line"></i>
                </Button>
                {pages.map((p) => (
                    <Button key={p} variant={p === page ? 'primary' : 'outline-secondary'} onClick={() => onPage(p)}>
                        {p}
                    </Button>
                ))}
                <Button variant="outline-secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
                    <i className="ri-arrow-right-s-line"></i>
                </Button>
            </div>
        </div>
    );
};

const Floating = ({ label, required, hint, children }) => (
    <div className="form-floating">
        {children}
        <Form.Label>
            {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {hint && <small className="text-muted d-block mt-1">{hint}</small>}
    </div>
);

const StepTitle = ({ n, icon, title }) => (
    <h6 className="uca-step-title">
        <span className="uca-step-badge">{n}</span>
        <i className={`${icon} me-2 text-primary`}></i>
        {title}
    </h6>
);

/* ------------------------------ add users to an existing coupon ------------------------------ */
// Same three modes as the "Allocate New Coupon" form: select manually, upload Excel, or filter users.
const AddUsersBox = ({ couponId, headers, onAllocated }) => {
    const [mode, setMode] = useState('manual');
    const [saving, setSaving] = useState(false);

    // manual
    const [userOptions, setUserOptions] = useState([]);
    const [newUsers, setNewUsers] = useState([]);
    const [searching, setSearching] = useState(false);

    // upload
    const [file, setFile] = useState(null);

    // filter
    const [filters, setFilters] = useState(INITIAL_USER_FILTERS);
    const [outlets, setOutlets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const mastersLoaded = useRef(false);
    const specialType = USER_FILTER_SPECIAL_TYPES.includes(filters.filter);

    const searchUsers = useMemo(
        () =>
            debounce(async (q) => {
                if (!q) return setUserOptions([]);
                setSearching(true);
                try {
                    const res = await axiosInstance.get(`user?search=${encodeURIComponent(q)}`, { headers });
                    if (res?.data?.status === true) {
                        const list = res.data.data?.users || [];
                        setUserOptions(list.map((u) => ({ value: u._id, label: `${u.name} (${u.mobile})` })));
                    }
                } catch (e) {
                    notify.error(getErr(e, 'Failed to search users'));
                } finally {
                    setSearching(false);
                }
            }, 500),
        [headers]
    );
    useEffect(() => () => searchUsers.cancel(), [searchUsers]);

    // load outlet / category / product lists only when the Filter mode is first opened
    useEffect(() => {
        if (mode !== 'filter' || mastersLoaded.current) return;
        mastersLoaded.current = true;
        const get = async (url, onOk) => {
            try {
                const res = await axiosInstance.get(url, { headers });
                if (res?.data?.status === true) onOk(res.data.data || {});
            } catch (e) {
                notify.error(getErr(e, 'Failed to load filter data'));
            }
        };
        get('outlet', (d) => setOutlets(d.outlets || []));
        get('masters/category/get', (d) => setCategories(d.categories || []));
        get('product', (d) => setProducts(d.products || []));
    }, [mode, headers]);

    const outletOpts = outlets.map((o) => ({ value: o._id, label: `${o.name} (${o.city})` }));
    const categoryOpts = categories.map((c) => ({ value: c._id, label: c.name }));
    const productOpts = products
        .filter((p) => p?.isFreebieProduct === false)
        .filter((p) => !filters.category || p?.category?._id === filters.category)
        .map((p) => ({ value: p._id, label: p.name }));

    const changeMode = (m) => {
        setMode(m);
        setNewUsers([]);
        setFile(null);
        setFilters(INITIAL_USER_FILTERS);
    };

    const changeFilter = (field, value) =>
        setFilters((prev) => {
            const u = { ...prev, [field]: value };
            if (field === 'filter' && USER_FILTER_SPECIAL_TYPES.includes(value)) {
                Object.assign(u, { minPrice: '', maxPrice: '', category: '', product: '', foodType: '' });
            }
            if (field === 'category') u.product = '';
            return u;
        });

    const buildFilterPayload = () => {
        const p = {};
        const { startDate, endDate, minPrice, maxPrice, outlet, foodType, filter, category, product, search, sortType, sortBy } = filters;
        if (startDate) p.startDate = format(startDate, 'yyyy-MM-dd');
        if (endDate) p.endDate = format(endDate, 'yyyy-MM-dd');
        if (minPrice !== '') p.minPrice = minPrice;
        if (maxPrice !== '') p.maxPrice = maxPrice;
        if (outlet) p.outlet = outlet;
        if (foodType) p.foodType = foodType;
        if (filter) p.filter = filter;
        if (category) p.category = category;
        if (product) p.product = product;
        if (search) p.search = search;
        if (sortType) p.sortType = sortType;
        if (sortBy) p.sortBy = sortBy;
        return p;
    };

    const handleFile = (f) => {
        if (!f) return notify.error('Please select a file');
        const allowed = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        if (!allowed.includes(f.type)) return notify.error('Please upload a valid Excel file (.xls or .xlsx)');
        setFile(f);
    };

    const handleAllocate = async () => {
        setSaving(true);
        try {
            let res;
            if (mode === 'manual') {
                if (!newUsers.length) return notify.error('Select at least one user');
                res = await axiosInstance.post(
                    'user-coupon-allocation/usersallocation',
                    { coupon_id: couponId, users: newUsers.map((u) => u.value) },
                    { headers }
                );
            } else if (mode === 'upload') {
                if (!file) return notify.error('Please upload an Excel file with users');
                const fd = new FormData();
                fd.append('coupon_id', couponId);
                fd.append('allocatedUsers', file);
                res = await axiosInstance.post('user-coupon-allocation', fd, {
                    headers: { ...headers, 'Content-Type': undefined },
                });
            } else {
                const payload = buildFilterPayload();
                const hasCriteria = Object.keys(payload).some((k) => !['sortBy', 'sortType'].includes(k));
                if (!hasCriteria) return notify.error('Select at least one filter');
                res = await axiosInstance.post('user-coupon-allocation', { coupon_id: couponId, ...payload }, { headers });
            }

            if (res?.data?.status === true) {
                notify.success(res?.data?.data?.message || 'Users allocated successfully');
                onAllocated();
            }
        } catch (e) {
            notify.error(getSubmitErr(e, 'Failed to allocate users'));
        } finally {
            setSaving(false);
        }
    };

    const modeBtn = (m, icon, text) => (
        <Button size="sm" variant={mode === m ? 'primary' : 'outline-secondary'} onClick={() => changeMode(m)}>
            <i className={`${icon} me-1`}></i> {text}
        </Button>
    );

    const canSubmit = mode === 'manual' ? newUsers.length > 0 : mode === 'upload' ? !!file : true;

    return (
        <div className="uca-add-box">
            <div className="btn-group mb-3" role="group">
                {modeBtn('manual', 'ri-user-search-line', 'Select Manually')}
                {modeBtn('upload', 'ri-upload-cloud-line', 'Upload Excel')}
                {/* {modeBtn('filter', 'ri-filter-3-line', 'Filter Users')} */}
            </div>

            {mode === 'manual' && (
                <>
                    <Select
                        isMulti
                        options={userOptions}
                        value={newUsers}
                        onChange={(v) => setNewUsers(v || [])}
                        onInputChange={(q, meta) => { if (meta.action === 'input-change') searchUsers(q); }}
                        filterOption={() => true}
                        isLoading={searching}
                        closeMenuOnSelect={false}
                        placeholder="Search users by name or mobile..."
                        noOptionsMessage={({ inputValue }) => (inputValue ? 'No users found' : 'Type to search')}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                    />
                    <small className="text-muted d-block mt-2">{newUsers.length} selected</small>
                </>
            )}

            {mode === 'upload' && (
                <>
                    <Form.Control type="file" accept=".xls, .xlsx" onChange={(e) => handleFile(e.target.files[0])} />
                    <small className="text-muted d-block mt-2">
                        Upload an Excel file (.xls or .xlsx) with users. The file is sent to the server for processing.
                    </small>
                    {file && (
                        <div className="alert alert-success mt-2 py-2 mb-0 d-flex justify-content-between align-items-center">
                            <span><i className="ri-check-line me-2"></i>{file.name}</span>
                            <Button variant="link" size="sm" className="text-danger p-0" onClick={() => setFile(null)}>
                                <i className="ri-close-line me-1"></i> Remove
                            </Button>
                        </div>
                    )}
                </>
            )}

            {mode === 'filter' && (
                <>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Outlet</Form.Label>
                            <Select
                                options={outletOpts}
                                value={outletOpts.find((o) => o.value === filters.outlet) || null}
                                onChange={(o) => changeFilter('outlet', o ? o.value : '')}
                                isClearable
                                placeholder="Select outlet"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Start Date</Form.Label>
                            <DatePicker
                                selected={filters.startDate}
                                onChange={(d) => changeFilter('startDate', d)}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Start Date"
                                className="form-control"
                                maxDate={filters.endDate || new Date()}
                                popperClassName="datepicker-popper"
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">End Date</Form.Label>
                            <DatePicker
                                selected={filters.endDate}
                                onChange={(d) => changeFilter('endDate', d)}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="End Date"
                                className="form-control"
                                minDate={filters.startDate}
                                maxDate={new Date()}
                                popperClassName="datepicker-popper"
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">User Type</Form.Label>
                            <Form.Select value={filters.filter} onChange={(e) => changeFilter('filter', e.target.value)}>
                                <option value="">Select</option>
                                <option value="JOINING_COUPON_ONE_ORDER">Joining Coupon Users (1 order)</option>
                                <option value="COUPON_ORDERS">Coupon Users</option>
                                <option value="NO_COUPON_ORDERS">Non-Coupon Users</option>
                                <option value="REFERRING_USERS">Referring Users</option>
                                <option value="REFERRED_USERS">Referred Users</option>
                                <option value="REPEATED_USERS_DAYS">Repeated Users (By Days)</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Min Price</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                placeholder="Min Price"
                                value={filters.minPrice}
                                onChange={(e) => changeFilter('minPrice', e.target.value)}
                                disabled={specialType}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Max Price</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                placeholder="Max Price"
                                value={filters.maxPrice}
                                onChange={(e) => changeFilter('maxPrice', e.target.value)}
                                disabled={specialType}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Category</Form.Label>
                            <Select
                                options={categoryOpts}
                                value={categoryOpts.find((o) => o.value === filters.category) || null}
                                onChange={(o) => changeFilter('category', o ? o.value : '')}
                                isClearable
                                isDisabled={specialType}
                                placeholder="Select category"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Product</Form.Label>
                            <Select
                                options={productOpts}
                                value={productOpts.find((o) => o.value === filters.product) || null}
                                onChange={(o) => changeFilter('product', o ? o.value : '')}
                                isClearable
                                isDisabled={specialType}
                                placeholder="Select product"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Preferred Taste</Form.Label>
                            <Form.Select
                                value={filters.foodType}
                                onChange={(e) => changeFilter('foodType', e.target.value)}
                                disabled={specialType}
                            >
                                <option value="">Select</option>
                                <option value="FOOD">Food</option>
                                <option value="BEVERAGE">Beverage</option>
                                <option value="COMBOS">Combos</option>
                                <option value="DESSERTS">Desserts</option>
                                <option value="EXTRAS">Extras</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-semibold">Search (name / mobile)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search by name or mobile"
                                value={filters.search}
                                onChange={(e) => changeFilter('search', e.target.value)}
                            />
                        </Col>
                    </Row>
                    <small className="text-muted mt-2 d-block">
                        <i className="ri-information-line me-1"></i>
                        The backend matches these filters and allocates the coupon to all eligible users.
                    </small>
                </>
            )}

            <div className="d-flex justify-content-end mt-3">
                <Button size="sm" onClick={handleAllocate} disabled={saving || !canSubmit}>
                    {saving ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="ri-check-line me-1"></i>}
                    Allocate
                </Button>
            </div>
        </div>
    );
};

/* ------------------------------ coupon (view) modal ------------------------------ */
const CouponModal = ({ show, coupon, onHide, canAdd, canDelete, canEdit, onEdit }) => {
    const accessToken = localStorage.getItem('authToken');
    const headers = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
    const couponId = coupon?._id;

    const [tab, setTab] = useState('details');
    const [openScope, setOpenScope] = useState(null); // which applicability tile is expanded
    const [codeCopied, setCodeCopied] = useState(false);

    const [details, setDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1 });
    const [totalAllocated, setTotalAllocated] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [showAdd, setShowAdd] = useState(false);

    // Reset + refetch whenever the modal (re)opens — also refreshes after an edit
    useEffect(() => {
        if (!show) return;
        setTab('details');
        setOpenScope(null);
        setPage(1);
        setSearchInput('');
        setSearch('');
        setTotalAllocated(0);
        setSelectedIds([]);
        setConfirmDelete(false);
        setShowAdd(false);
        setDetails(null);
    }, [show, couponId]);

    useEffect(() => {
        const t = setTimeout(() => {
            const q = searchInput.trim();
            setSearch((prev) => {
                if (prev !== q) setPage(1);
                return q;
            });
        }, 500);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (!show || !couponId) return;
        let cancelled = false;
        (async () => {
            setDetailsLoading(true);
            try {
                const res = await axiosInstance.get(`user-coupon-allocation/allocated-coupon-details?coupon_id=${couponId}`, { headers });
                if (!cancelled && res?.data?.status === true) {
                    const d = res.data.data || {};
                    setDetails(d.coupon || d.couponDetails || d.allocatedCouponDetails || d);
                }
            } catch (e) {
                if (!cancelled) notify.error(getErr(e, 'Failed to fetch coupon details'));
            } finally {
                if (!cancelled) setDetailsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [show, couponId, headers]);

    const fetchUsers = useCallback(async () => {
        if (!couponId) return;
        setUsersLoading(true);
        try {
            const params = new URLSearchParams({ coupon_id: couponId, page, limit });
            if (search) params.append('search', search);
            const res = await axiosInstance.get(`user-coupon-allocation/users?${params.toString()}`, { headers });
            if (res?.data?.status === true) {
                const d = res.data.data || {};
                const list = d.users || d.allocatedUsers || d.userCouponAllocationData || [];
                const p = d.pagination || {};
                const totalRecords = p.totalRecords ?? list.length;
                setUsers(list.map(normalizeUser));
                setPagination({
                    totalRecords,
                    totalPages: Math.max(1, p.totalPages ?? Math.ceil(totalRecords / limit)),
                });
                if (!search) setTotalAllocated(totalRecords);
                setSelectedIds([]);
                setConfirmDelete(false);
            }
        } catch (e) {
            notify.error(getErr(e, 'Failed to fetch allocated users'));
        } finally {
            setUsersLoading(false);
        }
    }, [couponId, page, limit, search, headers]);

    useEffect(() => {
        if (show) fetchUsers();
    }, [show, fetchUsers]);

    const handleDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            const res = await axiosInstance.delete('user-coupon-allocation/usersallocation', {
                data: { coupon_id: couponId, users: selectedIds },
                headers,
            });
            if (res?.data?.status === true) {
                notify.success(res?.data?.data?.message || 'Users removed successfully');
                if (selectedIds.length >= users.length && page > 1) setPage(page - 1);
                else fetchUsers();
            }
        } catch (e) {
            notify.error(getErr(e, 'Failed to remove users'));
        } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const toggleOne = (id) =>
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const allSelected = users.length > 0 && selectedIds.length === users.length;
    const toggleAll = () => setSelectedIds(allSelected ? [] : users.map((u) => u.id));

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(coupon?.couponCode || details?.couponCode || '');
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 1200);
        } catch {
            notify.error('Copy failed');
        }
    };

    /* ------------ details view model ------------ */
    const c = details || {};
    const active = isCouponActive(c);
    const money = (v) => (v ? `₹ ${v}` : 'Not specified');
    const days = Array.isArray(c.dayOfWeek) && c.dayOfWeek.length ? c.dayOfWeek.map((d) => DAYS[d]).join(', ') : 'Any day';
    const isHiddenCoupon = c.isHidden === true || c.isHidden === 'true';

    const enabledTypes = [
        ['BOGO', c.isBogoCoupon],
        ['Size Upgrade', !!(c.newSizeUpgradeAddon || c.sizeUpgradeAddon)],
        ['Combo', c.isComboCategoryCoupon],
        ['MRP', c.isMrpDiscount],
        ['Freebie', c.isFreebieCoupon],
        ['Joining', c.isJoiningCoupon],
        ['Referral', c.isReferralCoupon],
        ['Birthday', c.isBirthDayCoupon],
        ['Event', c.isEventCoupon],
        ['Cashback', c.isCashbackCoupon],
    ].filter(([, on]) => on);

    // extra discount rows only when they are actually set
    const extraDiscount = [
        ['ri-price-tag-2-line', 'Discount Price', c.discountPrice ? `₹ ${c.discountPrice}` : null],
        ['ri-stack-line', 'Quantity Type', c.applicableQuantityType],
        ['ri-add-circle-line', 'Max Addons Allowed', c.maxAddonsAllowed],
        ['ri-gift-line', 'Freebie', c.isFreebieCoupon ? `${labelOf(c.freebieProduct) || 'N/A'} × ${c.freebieQuantity || 1}` : null],
        ['ri-arrow-up-circle-line', 'Size Upgrade Addon', labelOf(c.newSizeUpgradeAddon || c.sizeUpgradeAddon) || null],
    ].filter(([, , v]) => v !== null && v !== undefined && v !== '');

    // applicability tiles: [key, icon, label, items, tone, singular, plural]
    const scopeDefs = [
        ['cats', 'ri-folder-3-line', 'Applicable Categories', labels(c.applicableCategories), 'primary', 'category', 'categories'],
        ['prods', 'ri-shopping-bag-3-line', 'Applicable Products', labels(c.applicableProducts), 'primary', 'product', 'products'],
        ['inOut', 'ri-map-pin-2-line', 'Included Outlets', labels(c.includedOutlets), 'success', 'outlet', 'outlets'],
        ['exOut', 'ri-map-pin-2-line', 'Excluded Outlets', labels(c.excludedOutlets), 'danger', 'outlet', 'outlets'],
        ['exProd', 'ri-forbid-line', 'Excluded Products', [...labels(c.excludedProducts), ...labels(c.manuallyExcludedProducts)], 'danger', 'product', 'products'],
        ['depProd', 'ri-node-tree', 'Dependent Products', labels(c.dependantProducts), 'warning', 'product', 'products'],
        ['depCat', 'ri-node-tree', 'Dependent Categories', labels(c.dependantCategories), 'warning', 'category', 'categories'],
        ['pCombo', 'ri-links-line', 'Primary Combo Categories', labels(c.primaryComboCategories), 'info', 'category', 'categories'],
        ['sCombo', 'ri-links-line', 'Secondary Combo Categories', labels(c.secondaryComboCategories), 'info', 'category', 'categories'],
        ['levels', 'ri-medal-line', 'Applicable Levels', labels(c.applicableLevels), 'primary', 'level', 'levels'],
        ['exAddon', 'ri-list-settings-line', 'Excluded Addons', labels(c.excludedAddons), 'danger', 'addon', 'addons'],
        ['depAddon', 'ri-list-settings-line', 'Dependent Addons', labels(c.dependantAddonInfo), 'warning', 'addon', 'addons'],
    ].filter(([, , , items]) => items.length > 0);
    const openDef = scopeDefs.find(([k]) => k === openScope);
    const noProductScope = !labels(c.applicableCategories).length && !labels(c.applicableProducts).length;

    const goToUsers = () => setTab('users');

    return (
        <Modal show={show} onHide={onHide} centered scrollable size="xl" className="uca-modal ucm">
            {/* ---------- header ---------- */}
            <div className="ucm-head">
                <div className="ucm-head-icon"><i className="ri-coupon-3-fill"></i></div>

                <div className="ucm-head-main">
                    <div className="ucm-title-row">
                        <h4 className="ucm-title" title={coupon?.title}>{coupon?.title}</h4>
                        <button type="button" className="ucm-icon-btn" onClick={copyCode} title="Copy code">
                            <i className={codeCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                        </button>
                    </div>

                    <div className="ucm-chip-row">
                        <span className="ucm-code-label">Coupon Code</span>
                        <span className="ucm-code-pill">{coupon?.couponCode}</span>

                        {details && (
                            <span className={`ucm-pill ${active ? 'green' : 'red'}`}>
                                <Dot tone={active ? 'green' : 'red'} />{active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                        )}
                        {details && c.isCouponAllocated && (
                            <span className="ucm-pill green"><i className="ri-user-follow-line"></i>ALLOCATED</span>
                        )}
                        {details && c.discountPercent ? (
                            <span className="ucm-pill discount"><i className="ri-percent-line"></i>{c.discountPercent}% OFF</span>
                        ) : null}
                        {enabledTypes.map(([l]) => <span key={l} className="ucm-pill soft">{l}</span>)}
                    </div>

                    <div className="ucm-subtitle">User Coupon Allocation</div>
                </div>

                <div className="ucm-head-actions">
                    {canEdit && details && (
                        <button type="button" className="ucm-btn outline" onClick={() => onEdit(details)}>
                            <i className="ri-edit-line"></i> Edit Coupon
                        </button>
                    )}
                    <button type="button" className="ucm-btn square" onClick={onHide} aria-label="Close">
                        <i className="ri-close-line"></i>
                    </button>
                </div>
            </div>

            {/* ---------- tabs ---------- */}
            <div className="ucm-tabs">
                <button type="button" className={tab === 'details' ? 'active' : ''} onClick={() => setTab('details')}>
                    <i className="ri-information-line"></i> Coupon Details
                </button>
                <button type="button" className={tab === 'users' ? 'active' : ''} onClick={goToUsers}>
                    <i className="ri-group-line"></i> Allocated Users
                    <span className="ucm-count">{totalAllocated.toLocaleString('en-IN')}</span>
                </button>
            </div>

            {/* ---------- body ---------- */}
            <Modal.Body className="ucm-body">
                {tab === 'details' && (
                    detailsLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : !details ? (
                        <div className="uca-empty"><i className="ri-inbox-line"></i><p>No details available</p></div>
                    ) : (
                        <div className="ucm-grid">
                            {/* Overview */}
                            <InfoCard icon="ri-information-fill" title="Overview">
                                <div className="ucm-items two">
                                    <InfoItem icon="ri-coupon-3-line" label="Coupon Code">
                                        <span className="d-flex align-items-center justify-content-between w-100 gap-2">
                                            {c.couponCode || 'N/A'}
                                            <button type="button" className="ucm-mini-btn" onClick={copyCode} title="Copy code">
                                                <i className={codeCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                                            </button>
                                        </span>
                                    </InfoItem>
                                    <InfoItem icon="ri-repeat-line" label="Usage Type" value={c.usageType || '—'} />
                                    <InfoItem icon="ri-shopping-cart-2-line" label="Order Type" value={c.orderType || 'ALL'} />
                                    <InfoItem icon="ri-record-circle-fill" label="Status">
                                        <span className="d-inline-flex align-items-center gap-2">
                                            <Dot tone={active ? 'green' : 'red'} />{c.status || (active ? 'ACTIVE' : 'INACTIVE')}
                                        </span>
                                    </InfoItem>
                                    <InfoItem
                                        icon={isHiddenCoupon ? 'ri-eye-off-line' : 'ri-eye-line'}
                                        label="Visibility"
                                        value={isHiddenCoupon ? 'Hidden from users' : 'Visible to users'}
                                    />
                                    <InfoItem icon="ri-team-line" label="Allocated Coupon" value={c.isCouponAllocated ? 'Yes' : 'No'} />
                                </div>
                            </InfoCard>

                            {/* Discount & Order Rules */}
                            <InfoCard icon="ri-price-tag-3-fill" title="Discount & Order Rules">
                                <div className="ucm-items two">
                                    <InfoItem icon="ri-percent-line" label="Discount Percentage" value={c.discountPercent ? `${c.discountPercent}% OFF` : 'Not specified'} />
                                    <InfoItem icon="ri-money-rupee-circle-line" label="Discount Amount" value={money(c.discountAmount)} />
                                    <InfoItem icon="ri-coins-line" label="Max Discount Amount" value={money(c.maxDiscountAmount)} />
                                    <InfoItem icon="ri-shopping-cart-line" label="Min Order Amount" value={money(c.minOrderAmount)} />
                                    {extraDiscount.map(([icon, l, v]) => <InfoItem key={l} icon={icon} label={l} value={v} />)}
                                </div>
                            </InfoCard>

                            {/* Validity & Schedule */}
                            <InfoCard icon="ri-calendar-event-fill" title="Validity & Schedule">
                                <div className="ucm-items three">
                                    <InfoItem icon="ri-calendar-line" label="Valid From" value={fmtDate(c.validityStartDate) || '—'} />
                                    <InfoItem icon="ri-calendar-check-line" label="Valid Till" value={fmtDate(c.validityEndDate) || '—'} />
                                    <InfoItem icon="ri-calendar-2-line" label="Applicable Days" value={days} />
                                    {(c.startTime || c.endTime) && (
                                        <InfoItem icon="ri-time-line" label="Time Window" value={`${c.startTime || '—'} – ${c.endTime || '—'}`} />
                                    )}
                                    {c.dailyUsageLimit ? <InfoItem icon="ri-speed-line" label="Daily Usage Limit" value={c.dailyUsageLimit} /> : null}
                                </div>
                            </InfoCard>

                            {/* Applicability */}
                            <InfoCard icon="ri-stack-fill" title="Applicability">
                                {scopeDefs.length === 0 ? (
                                    <div className="ucm-note"><i className="ri-global-line"></i>No restriction — applies to all products and outlets</div>
                                ) : (
                                    <>
                                        <div className="ucm-items two">
                                            {scopeDefs.map(([key, icon, label, items, , one, many]) => (
                                                <button
                                                    type="button"
                                                    key={key}
                                                    className={`ucm-item ucm-item-btn ${openScope === key ? 'open' : ''}`}
                                                    onClick={() => setOpenScope(openScope === key ? null : key)}
                                                    aria-expanded={openScope === key}
                                                >
                                                    <i className={`${icon} ucm-item-icon`}></i>
                                                    <div className="ucm-item-body">
                                                        <span className="ucm-label">{label}</span>
                                                        <span className="ucm-value">{items.length} {items.length === 1 ? one : many}</span>
                                                    </div>
                                                    <i className="ri-arrow-right-s-line ucm-chevron"></i>
                                                </button>
                                            ))}
                                        </div>
                                        {openDef && (
                                            <div className="ucm-expand">
                                                <div className="ucm-expand-head">
                                                    {openDef[2]}
                                                    <button type="button" className="ucm-mini-btn" onClick={() => setOpenScope(null)} aria-label="Collapse">
                                                        <i className="ri-close-line"></i>
                                                    </button>
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {openDef[3].map((it, i) => <span key={`${it}-${i}`} className={`uca-chip ${openDef[4]}`}>{it}</span>)}
                                                </div>
                                            </div>
                                        )}
                                        {noProductScope && (
                                            <div className="ucm-note mt-2"><i className="ri-global-line"></i>No category or product restriction — applies to all products</div>
                                        )}
                                    </>
                                )}
                            </InfoCard>

                            {/* Description */}
                            {c.description && (
                                <InfoCard icon="ri-file-text-fill" title="Description" className="span-2">
                                    <p className="ucm-text">{c.description}</p>
                                </InfoCard>
                            )}

                            {/* Terms */}
                            {c.termsAndConditions?.length > 0 && (
                                <InfoCard icon="ri-shield-check-fill" title="Terms & Conditions" className="span-2">
                                    <ol className="ucm-terms">
                                        {c.termsAndConditions.map((t, i) => <li key={i}>{t}</li>)}
                                    </ol>
                                </InfoCard>
                            )}

                            <div className="ucm-meta span-2">
                                {fmtDateTime(c.createdAt) && <span><i className="ri-time-line"></i>Created {fmtDateTime(c.createdAt)}</span>}
                                {fmtDateTime(c.updatedAt) && <span><i className="ri-refresh-line"></i>Updated {fmtDateTime(c.updatedAt)}</span>}
                            </div>
                        </div>
                    )
                )}

                {tab === 'users' && (
                    <>
                        <div className="uca-users-bar">
                            <div>
                                <h6 className="mb-0 fw-bold">Allocated Users</h6>
                                <small className="text-muted">
                                    {search
                                        ? `${pagination.totalRecords} match(es) of ${totalAllocated} allocated`
                                        : `${totalAllocated} user(s) have this coupon`}
                                </small>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="uca-search sm">
                                    <i className="ri-search-line"></i>
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        placeholder="Search by name, email or mobile"
                                    />
                                    {searchInput && (
                                        <button type="button" onClick={() => setSearchInput('')}><i className="ri-close-line"></i></button>
                                    )}
                                </div>
                                {canAdd && (
                                    <Button size="sm" variant={showAdd ? 'outline-secondary' : 'primary'} onClick={() => setShowAdd((s) => !s)}>
                                        <i className={`${showAdd ? 'ri-close-line' : 'ri-user-add-line'} me-1`}></i>
                                        {showAdd ? 'Cancel' : 'Add Users'}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {showAdd && (
                            <AddUsersBox
                                couponId={couponId}
                                headers={headers}
                                onAllocated={() => {
                                    setShowAdd(false);
                                    page === 1 ? fetchUsers() : setPage(1);
                                }}
                            />
                        )}

                        {canDelete && selectedIds.length > 0 && (
                            <div className="uca-select-bar">
                                {confirmDelete ? (
                                    <>
                                        <span>Remove {selectedIds.length} user(s) from this coupon?</span>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="light" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
                                            <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>
                                                {deleting && <Spinner size="sm" animation="border" className="me-2" />}
                                                Confirm
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <span>{selectedIds.length} selected</span>
                                        <div className="d-flex gap-2">
                                            <Button size="sm" variant="light" onClick={() => setSelectedIds([])}>Clear</Button>
                                            <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
                                                <i className="ri-delete-bin-line me-1"></i> Remove
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className={`uca-table-wrap ${usersLoading ? 'loading' : ''}`}>
                            {usersLoading && <div className="uca-table-loader"><Spinner animation="border" variant="primary" size="sm" /></div>}
                            {!usersLoading && users.length === 0 ? (
                                <div className="uca-empty">
                                    <i className="ri-user-search-line"></i>
                                    <p>{search ? `No users match "${search}"` : 'No users allocated yet'}</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle uca-table mb-0">
                                        <thead>
                                            <tr>
                                                {canDelete && (
                                                    <th style={{ width: 40 }}>
                                                        <Form.Check checked={allSelected} onChange={toggleAll} />
                                                    </th>
                                                )}
                                                <th>User</th>
                                                <th>Mobile</th>
                                                <th>Email</th>
                                                <th>Gender</th>
                                                <th>D.O.B</th>
                                                {canDelete && <th style={{ width: 50 }}></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((u) => (
                                                <tr key={u.id} className={selectedIds.includes(u.id) ? 'selected' : ''}>
                                                    {canDelete && (
                                                        <td><Form.Check checked={selectedIds.includes(u.id)} onChange={() => toggleOne(u.id)} /></td>
                                                    )}
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="uca-user-avatar" style={{ background: gradientFor(u.id) }}>{initials(u.name)}</span>
                                                            <span className="fw-semibold">{u.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>{u.mobile}</td>
                                                    <td className="text-muted">{u.email}</td>
                                                    <td>{u.gender}</td>
                                                    <td>{u.dob}</td>
                                                    {canDelete && (
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="uca-row-del"
                                                                title="Remove user"
                                                                onClick={() => { setSelectedIds([u.id]); setConfirmDelete(true); }}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {pagination.totalRecords > 0 && (
                            <Pager
                                page={page}
                                limit={limit}
                                totalPages={pagination.totalPages}
                                totalRecords={pagination.totalRecords}
                                onPage={setPage}
                                onLimit={(n) => { setLimit(n); setPage(1); }}
                            />
                        )}
                    </>
                )}
            </Modal.Body>

            {/* ---------- footer (details tab only) ---------- */}
            {tab === 'details' && details && (
                <Modal.Footer className="ucm-foot">
                    {canEdit && (
                        <button type="button" className="ucm-btn outline" onClick={() => onEdit(details)}>
                            <i className="ri-edit-line"></i> Edit Coupon
                        </button>
                    )}
                    <button type="button" className="ucm-btn primary" onClick={goToUsers}>
                        <i className="ri-eye-line"></i> View Allocation
                    </button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

/* ------------------------- create / edit coupon form modal ------------------------- */
const INITIAL_COUPON = {
    isBogoCoupon: false,
    isSizeUpgradeCoupon: false,
    newSizeUpgradeAddon: '',
    title: '',
    description: '',
    usageType: '',
    orderType: 'ALL',
    couponCode: '',
    discountPercent: '',
    discountAmount: '',
    validityStartDate: '',
    validityEndDate: '',
    dailyUsageLimit: '',
    startTime: '',
    endTime: '',
    discountPrice: '',
    applicableQuantityType: '',
    termsAndConditions: [],
    isComboCategoryCoupon: false,
    maxAddonsAllowed: null,
    maxDiscountAmount: '',
    minOrderAmount: '',
    isMrpDiscount: false,
    isFreebieCoupon: false,
    freebieProduct: '',
    freebieQuantity: '',
    excludedAddons: [],
    isHidden: 'false',
};

const INITIAL_SELECTIONS = {
    selectedIncludedOutlets: [],
    selectedExcludedOutlets: [],
    selectedApplicableCategories: [],
    selectedApplicableProducts: [],
    selectedDependentCategories: [],
    selectedDependentProducts: [],
    selectedExcludedProducts: [],
    selectedApplicableLevels: [],
    primaryComboCategories: [],
    secondaryComboCategories: [],
};

const COUPON_TYPES = [
    { id: 'isBogoCoupon', label: 'Bogo Coupon', field: 'isBogoCoupon', icon: 'ri-shopping-basket-fill' },
    { id: 'isUniversalCoupon', label: 'Universal Coupon', field: 'isUniversalCoupon', icon: 'ri-global-line' },
    { id: 'isSizeUpgradeCoupon', label: 'Size Upgrade', field: 'isSizeUpgradeCoupon', icon: 'ri-arrow-up-line' },
    { id: 'isComboCategoryCoupon', label: 'Combo Coupon', field: 'isComboCategoryCoupon', icon: 'ri-links-line' },
    { id: 'isMRPCoupon', label: 'MRP Coupon', field: 'isMrpDiscount', icon: 'ri-price-tag-3-line' },
    { id: 'isFreebieCoupon', label: 'Freebie Coupon', field: 'isFreebieCoupon', icon: 'ri-gift-line' },
];

// Map an existing coupon (details API shape) into form state
const prefillFromCoupon = (c) => {
    const excludedAddons = (Array.isArray(c.excludedAddons) ? c.excludedAddons : [])
        .filter((x) => x && typeof x === 'object' && (x.addonId || x.addonValueId))
        .map((x) => ({
            addonId: idOf(x.addonId),
            addonValueId: idOf(x.addonValueId),
            addonTitle: x.addonTitle,
            addonValueTitle: x.addonValueTitle,
        }));

    const addonGroups = [];
    excludedAddons.forEach((x) => {
        if (x.addonId && !addonGroups.some((g) => g.value === x.addonId)) {
            addonGroups.push({ value: x.addonId, label: x.addonTitle || '' });
        }
    });

    const sizeAddon = c.newSizeUpgradeAddon || c.sizeUpgradeAddon;

    return {
        couponData: {
            ...INITIAL_COUPON,
            title: nz(c.title),
            description: nz(c.description),
            usageType: nz(c.usageType),
            orderType: c.orderType || 'ALL',
            couponCode: nz(c.couponCode),
            discountPercent: nz(c.discountPercent),
            discountAmount: nz(c.discountAmount),
            discountPrice: nz(c.discountPrice),
            maxDiscountAmount: nz(c.maxDiscountAmount),
            minOrderAmount: nz(c.minOrderAmount),
            dailyUsageLimit: nz(c.dailyUsageLimit),
            startTime: nz(c.startTime),
            endTime: nz(c.endTime),
            applicableQuantityType: nz(c.applicableQuantityType),
            termsAndConditions: Array.isArray(c.termsAndConditions) ? [...c.termsAndConditions] : [],
            isBogoCoupon: !!c.isBogoCoupon,
            isComboCategoryCoupon: !!c.isComboCategoryCoupon,
            isMrpDiscount: !!c.isMrpDiscount,
            isFreebieCoupon: !!c.isFreebieCoupon,
            freebieProduct: idOf(c.freebieProduct) || '',
            freebieQuantity: nz(c.freebieQuantity),
            newSizeUpgradeAddon: idOf(sizeAddon) || '',
            isSizeUpgradeCoupon: !!sizeAddon,
            maxAddonsAllowed: c.maxAddonsAllowed ?? null,
            excludedAddons,
            isHidden: String(c.isHidden === true || c.isHidden === 'true'),
            ...(c.isUniversalCoupon ? { isUniversalCoupon: true } : {}),
        },
        selections: {
            selectedIncludedOutlets: toOpts(c.includedOutlets),
            selectedExcludedOutlets: toOpts(c.excludedOutlets),
            selectedApplicableCategories: toOpts(c.applicableCategories),
            selectedApplicableProducts: toOpts(c.applicableProducts),
            selectedDependentCategories: toOpts(c.dependantCategories),
            selectedDependentProducts: toOpts(c.dependantProducts),
            selectedExcludedProducts: toOpts(c.excludedProducts),
            selectedApplicableLevels: toOpts(c.applicableLevels),
            primaryComboCategories: toOpts(c.primaryComboCategories),
            secondaryComboCategories: toOpts(c.secondaryComboCategories),
        },
        universal: !!c.isUniversalCoupon,
        fromDate: c.validityStartDate ? dateOnly(c.validityStartDate) : null,
        toDate: c.validityEndDate ? dateOnly(c.validityEndDate) : null,
        days: (Array.isArray(c.dayOfWeek) ? c.dayOfWeek : []).map((d) => ({ value: d, label: DAYS[d] })),
        addonGroups,
        addonValues: excludedAddons.map((x) => ({
            value: x,
            label: `${x.addonTitle} - ${x.addonValueTitle}`,
        })),
    };
};

const CouponFormModal = ({ show, mode = 'create', initialCoupon, onHide, onDone }) => {
    const isEdit = mode === 'edit';
    const accessToken = localStorage.getItem('authToken');
    const headers = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

    // master data
    const [usersList, setUsersList] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [levels, setLevels] = useState([]);
    const [newSizeAddonsList, setNewSizeAddonsList] = useState([]);
    const [allAddonList, setAllAddonList] = useState([]);

    // form state
    const [couponData, setCouponData] = useState(INITIAL_COUPON);
    const [selections, setSelections] = useState(INITIAL_SELECTIONS);
    const [universalCoupon, setUniversalCoupon] = useState(false);
    const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [currentTerm, setCurrentTerm] = useState('');
    const [selectedAddonGroups, setSelectedAddonGroups] = useState([]);
    const [selectedAddonValues, setSelectedAddonValues] = useState([]);

    // step 1 state (create only)
    const [userSelectionMode, setUserSelectionMode] = useState('manual');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [file, setFile] = useState(null);
    const [userFilters, setUserFilters] = useState(INITIAL_USER_FILTERS);
    const isUserFilterSpecialTypeSelected = USER_FILTER_SPECIAL_TYPES.includes(userFilters.filter);

    const [isLoading, setIsLoading] = useState(false);
    const loadedRef = useRef(false);

    /* ---- options ---- */
    const usersInfo = usersList.map((u) => ({ value: u?._id, label: `${u?.name} (${u?.mobile})` }));
    const freebieProductsOptions = productsList
        .filter((p) => p.isFreebieProduct === true)
        .map((p) => ({ value: p._id, label: p.name }));
    const productsInfo = productsList
        .filter((p) => p?.isFreebieProduct === false)
        .map((p) => ({ value: p._id, label: p.name }));
    const categoriesData = categoryList.map((c) => ({ value: c._id, label: c.name }));
    const outletData = outlets
        .filter(
            (o) =>
                !selections.selectedExcludedOutlets?.some((s) => s.value === o._id) &&
                !selections.selectedIncludedOutlets?.some((s) => s.value === o._id)
        )
        .map((o) => ({ value: o._id, label: `${o.name} (${o.city})` }));
    const levelsData = levels.map((l) => ({ value: l._id, label: `${l.sequenceNo}. ${l.title}` }));
    const userFilterOutletOptions = outlets.map((o) => ({ value: o._id, label: `${o.name} (${o.city})` }));
    const userFilterProductOptions = productsList
        .filter((p) => p?.isFreebieProduct === false)
        .filter((p) => !userFilters.category || p?.category?._id === userFilters.category)
        .map((p) => ({ value: p._id, label: p.name }));

    // addon values are derived from the selected groups (works for prefilled edit data too)
    const filteredAddonValues = useMemo(() => {
        const ids = selectedAddonGroups.map((o) => o.value);
        return allAddonList
            .filter((a) => ids.includes(a._id))
            .flatMap((a) =>
                (a.addonValues || []).map((v) => ({
                    value: { addonId: a._id, addonValueId: v._id, addonTitle: a.title, addonValueTitle: v.value },
                    label: `${a.title} - ${v.value}`,
                }))
            );
    }, [allAddonList, selectedAddonGroups]);

    /* ---- data loading (lazy, once) ---- */
    const loadMasters = useCallback(() => {
        const get = async (url, onOk) => {
            try {
                const res = await axiosInstance.get(url, { headers });
                if (res?.data?.status === true) onOk(res.data.data || {});
            } catch (e) {
                notify.error(getErr(e, 'Failed to load form data'));
            }
        };
        get('product', (d) => setProductsList(d.products || []));
        get('masters/category/get', (d) => setCategoryList(d.categories || []));
        get('outlet', (d) => setOutlets(d.outlets || []));
        get('level', (d) => setLevels(d.levels || []));
        get('addons?page=1&limit=100', (d) => setAllAddonList(d.addons || []));
        get('product/getSizeUpgradeAddonsWithProducts', (d) => setNewSizeAddonsList(d.addons || []));
    }, [headers]);

    const searchUsers = useMemo(
        () =>
            debounce(async (q) => {
                if (!q) return setUsersList([]);
                try {
                    const res = await axiosInstance.get(`user?search=${encodeURIComponent(q)}`, { headers });
                    if (res?.data?.status === true) setUsersList(res.data.data?.users || []);
                } catch (e) {
                    notify.error(getErr(e, 'Failed to search users'));
                }
            }, 500),
        [headers]
    );
    useEffect(() => () => searchUsers.cancel(), [searchUsers]);

    /* ---- resets ---- */
    const resetCouponFields = () => {
        setCouponData(INITIAL_COUPON);
        setSelections(INITIAL_SELECTIONS);
        setUniversalCoupon(false);
        setFromDate(null);
        setToDate(null);
        setSelectedDaysOfWeek([]);
        setCurrentTerm('');
        setSelectedAddonGroups([]);
        setSelectedAddonValues([]);
    };

    const resetUserSelection = () => {
        setUserSelectionMode('manual');
        setSelectedUsers([]);
        setUsersList([]);
        setFile(null);
        setUserFilters(INITIAL_USER_FILTERS);
    };

    useEffect(() => {
        if (!show) return;
        if (!loadedRef.current) {
            loadedRef.current = true;
            loadMasters();
        }
        if (isEdit && initialCoupon) {
            const p = prefillFromCoupon(initialCoupon);
            setCouponData(p.couponData);
            setSelections(p.selections);
            setUniversalCoupon(p.universal);
            setFromDate(p.fromDate);
            setToDate(p.toDate);
            setSelectedDaysOfWeek(p.days);
            setSelectedAddonGroups(p.addonGroups);
            setSelectedAddonValues(p.addonValues);
            setCurrentTerm('');
            resetUserSelection();
        } else {
            resetCouponFields();
            resetUserSelection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // keep validity dates in coupon payload
    useEffect(() => {
        setCouponData((prev) => ({
            ...prev,
            validityStartDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
            validityEndDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
        }));
    }, [fromDate, toDate]);

    /* ---- handlers ---- */
    const handleCheckboxChange = (field) => (e) => {
        const checked = e.target.checked;
        resetCouponFields();
        setUniversalCoupon(field === 'isUniversalCoupon' ? checked : false);
        setCouponData({ ...INITIAL_COUPON, [field]: checked });
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        const v = name === 'couponCode' ? value.toUpperCase() : value;
        setCouponData((prev) => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || '' : v }));
    };

    const handleChange = (key, value) => setSelections((prev) => ({ ...prev, [key]: value }));

    const addTerm = () => {
        if (!currentTerm.trim()) return;
        setCouponData((prev) => ({
            ...prev,
            termsAndConditions: [...(Array.isArray(prev.termsAndConditions) ? prev.termsAndConditions : []), currentTerm],
        }));
        setCurrentTerm('');
    };

    const removeTerm = (index) =>
        setCouponData((prev) => ({
            ...prev,
            termsAndConditions: (prev.termsAndConditions || []).filter((_, i) => i !== index),
        }));

    const handleSelectAddonGroups = (options) => {
        const groups = options || [];
        setSelectedAddonGroups(groups);
        const ids = groups.map((o) => o.value);
        const keptValues = selectedAddonValues.filter((v) => ids.includes(v.value.addonId));
        setSelectedAddonValues(keptValues);
        setCouponData((prev) => ({ ...prev, excludedAddons: keptValues.map((o) => o.value) }));
    };

    const handleAddonValueChange = (options) => {
        setSelectedAddonValues(options || []);
        setCouponData((prev) => ({ ...prev, excludedAddons: options ? options.map((o) => o.value) : [] }));
    };

    const handleUserSelectionModeChange = (m) => {
        setUserSelectionMode(m);
        setSelectedUsers([]);
        setFile(null);
        setUserFilters(INITIAL_USER_FILTERS);
    };

    const handleUserFilterChange = (field, value) => {
        setUserFilters((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'filter' && USER_FILTER_SPECIAL_TYPES.includes(value)) {
                updated.minPrice = '';
                updated.maxPrice = '';
                updated.category = '';
                updated.product = '';
                updated.foodType = '';
            }
            if (field === 'category') updated.product = '';
            return updated;
        });
    };

    const buildUserFilterPayload = () => {
        const { startDate, endDate, minPrice, maxPrice, outlet, foodType, filter, category, product, search, sortType, sortBy } = userFilters;
        const payload = {};
        if (startDate) payload.startDate = format(startDate, 'yyyy-MM-dd');
        if (endDate) payload.endDate = format(endDate, 'yyyy-MM-dd');
        if (minPrice !== '' && minPrice !== null && minPrice !== undefined) payload.minPrice = minPrice;
        if (maxPrice !== '' && maxPrice !== null && maxPrice !== undefined) payload.maxPrice = maxPrice;
        if (outlet) payload.outlet = outlet;
        if (foodType) payload.foodType = foodType;
        if (filter) payload.filter = filter;
        if (category) payload.category = category;
        if (product) payload.product = product;
        if (search) payload.search = search;
        if (sortType) payload.sortType = sortType;
        if (sortBy) payload.sortBy = sortBy;
        return payload;
    };

    const handleFileUpload = (f) => {
        if (!f) return notify.error('Please select a file');
        const allowed = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        if (!allowed.includes(f.type)) return notify.error('Please upload a valid Excel file (.xls or .xlsx)');
        setFile(f);
        notify.success(`File selected: ${f.name}`);
    };

    /* ---- submit ---- */
    const allocateUsers = async (couponId) => {
        try {
            let response;
            if (userSelectionMode === 'upload') {
                const formData = new FormData();
                formData.append('coupon_id', couponId);
                formData.append('allocatedUsers', file);
                response = await axiosInstance.post('user-coupon-allocation', formData, {
                    headers: { ...headers, 'Content-Type': undefined },
                });
            } else if (userSelectionMode === 'filter') {
                response = await axiosInstance.post(
                    'user-coupon-allocation',
                    { coupon_id: couponId, ...buildUserFilterPayload() },
                    { headers }
                );
            } else {
                response = await axiosInstance.post(
                    'user-coupon-allocation',
                    { users: selectedUsers.map((u) => u.value), coupon_id: couponId },
                    { headers }
                );
            }

            if (response?.data?.status === true) {
                notify.success(response?.data?.data?.message || 'Coupon allocated successfully!');
                return true;
            }
            return false;
        } catch (error) {
            notify.error(getSubmitErr(error, 'Coupon created, but allocating users failed'));
            return false;
        }
    };

    // Same payload rules for create and edit. In edit, cleared fields are sent as null / [] so they actually clear.
    const buildBody = () => {
        const vals = (arr) => arr?.map((o) => o.value) || [];

        const payload = {
            ...couponData,
            excludedOutlets: vals(selections.selectedExcludedOutlets),
            includedOutlets: vals(selections.selectedIncludedOutlets),
            applicableLevels: vals(selections.selectedApplicableLevels),
            dayOfWeek: vals(selectedDaysOfWeek),
        };
        if (!isEdit) payload.isCouponAllocated = true;

        const lists = {
            primaryComboCategories: vals(selections.primaryComboCategories),
            secondaryComboCategories: vals(selections.secondaryComboCategories),
            dependantCategories: vals(selections.selectedDependentCategories),
            dependantProducts: vals(selections.selectedDependentProducts),
            excludedProductList: vals(selections.selectedExcludedProducts),
            applicableProducts: vals(selections.selectedApplicableProducts),
            applicableCategories: vals(selections.selectedApplicableCategories),
        };
        Object.entries(lists).forEach(([k, v]) => { if (isEdit || v.length > 0) payload[k] = v; });

        if (!isEdit) {
            Object.keys(payload).forEach((k) => {
                if (Array.isArray(payload[k]) && payload[k].length === 0) delete payload[k];
            });
        }

        const drop = (...keys) =>
            keys.forEach((k) => {
                if (isEdit) payload[k] = k.startsWith('is') ? false : null;
                else delete payload[k];
            });

        if (couponData.isFreebieCoupon) {
            drop('discountPercent', 'discountAmount', 'maxDiscountAmount', 'discountPrice', 'isMrpDiscount',
                'applicableQuantityType', 'isBogoCoupon', 'isSizeUpgradeCoupon', 'isComboCategoryCoupon');
        }
        if (payload.discountAmount) {
            drop('discountPercent', 'applicableProducts', 'isSizeUpgradeCoupon', 'sizeUpgradeAddon', 'applicableQuantityType');
        }
        if (payload.discountPercent) {
            drop('discountAmount', 'applicableProducts', 'isSizeUpgradeCoupon', 'sizeUpgradeAddon', 'applicableQuantityType');
        }
        if (payload.isBogoCoupon) {
            drop('applicableProducts', 'discountPercent', 'discountAmount', 'discountPrice', 'applicableQuantityType',
                'isSizeUpgradeCoupon', 'sizeUpgradeAddon');
        }
        if (payload.isSizeUpgradeCoupon) {
            drop('discountPercent', 'discountAmount', 'discountPrice', 'applicableQuantityType');
        }

        return Object.fromEntries(
            isEdit
                ? Object.entries(payload)
                    .filter(([, v]) => v !== undefined)
                    .map(([k, v]) => [k, v === '' ? null : v])
                : Object.entries(payload).filter(([, v]) => v !== null && v !== undefined && v !== '')
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isEdit) {
            if (userSelectionMode === 'manual' && selectedUsers.length === 0) return notify.error('Please select at least one user');
            if (userSelectionMode === 'upload' && !file) return notify.error('Please upload an Excel file with users');
        }
        if (!couponData.title?.trim()) return notify.error('Title is required');
        if (!couponData.couponCode?.trim()) return notify.error('Coupon Code is required');
        if (!couponData.validityStartDate || !couponData.validityEndDate) return notify.error('Please provide both start and end dates');

        if (couponData.isFreebieCoupon) {
            if (!couponData.freebieProduct) return notify.error('Please select a freebie product');
            if (!couponData.freebieQuantity || couponData.freebieQuantity <= 0) return notify.error('Please enter a valid freebie quantity');
        }

        if (!couponData.isFreebieCoupon && !couponData.isBogoCoupon && !couponData.isSizeUpgradeCoupon) {
            if (!(couponData.discountPercent || couponData.discountAmount || couponData.discountPrice)) {
                return notify.error('Please enter discount amount, percentage, or price');
            }
        }

        setIsLoading(true);
        try {
            const body = buildBody();

            if (isEdit) {
                const response = await updateCouponApi({ ...body, _id: initialCoupon?._id }, headers);
                if (response?.data?.status === true) {
                    notify.success(response?.data?.data?.message || 'Coupon updated successfully!');
                    onDone?.({ title: couponData.title, couponCode: couponData.couponCode });
                    onHide();
                }
                return;
            }

            const response = await axiosInstance.post('coupons', body, { headers });
            if (response?.data?.status === true) {
                const couponId = response?.data?.data?.dbResponse?._id;
                const ok = await allocateUsers(couponId);
                if (ok) {
                    onDone?.();
                    onHide();
                }
            }
        } catch (error) {
            notify.error(getSubmitErr(error, isEdit ? 'Failed to update coupon' : 'An error occurred while creating coupon'));
        } finally {
            setIsLoading(false);
        }
    };

    const modeBtn = (m, icon, text) => (
        <Button
            variant={userSelectionMode === m ? 'primary' : 'outline-secondary'}
            onClick={() => handleUserSelectionModeChange(m)}
        >
            <i className={`${icon} me-1`}></i> {text}
        </Button>
    );

    const step = (n) => (isEdit ? n - 1 : n);
    const noDiscountFields = couponData.isFreebieCoupon || couponData.isBogoCoupon || couponData.isSizeUpgradeCoupon;
    const comboLocked = couponData.isBogoCoupon || couponData.isSizeUpgradeCoupon || couponData.isMrpDiscount || couponData.isFreebieCoupon;
    const dependencyLocked = couponData.isBogoCoupon || couponData.isSizeUpgradeCoupon || couponData.isComboCategoryCoupon || couponData.isFreebieCoupon;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            backdrop="static"
            dialogClassName="modal-fullscreen-lg-down"
            className="uca-form-modal"
        >
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold">
                    <i className={`${isEdit ? 'ri-edit-box-line' : 'ri-coupon-add-line'} me-2 text-primary`}></i>
                    {isEdit ? `Edit Coupon${initialCoupon?.title ? ` — ${initialCoupon.title}` : ''}` : 'Allocate New Coupon'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Row className="g-3">
                        {/* ---------------- Step 1: users (create only) ---------------- */}
                        {!isEdit && (
                            <div className="mb-2">
                                <StepTitle n={1} icon="ri-user-add-line" title="Select Users" />

                                <div className="btn-group mb-3" role="group">
                                    {modeBtn('manual', 'ri-user-search-line', 'Select Manually')}
                                    {modeBtn('upload', 'ri-upload-cloud-line', 'Upload Excel')}
                                    {modeBtn('filter', 'ri-filter-3-line', 'Filter Users')}
                                </div>

                                <Row className="g-2">
                                    {userSelectionMode === 'upload' && (
                                        <Col md={12} className="mb-2">
                                            <div className="card border">
                                                <div className="card-body">
                                                    <Form.Label className="fw-semibold">
                                                        <i className="ri-upload-cloud-line me-2"></i>Upload Users File
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        accept=".xls, .xlsx"
                                                        onChange={(e) => handleFileUpload(e.target.files[0])}
                                                    />
                                                    <small className="text-muted mt-2 d-block">
                                                        Upload an Excel file (.xls or .xlsx) with users. The file itself is sent to the server for processing.
                                                    </small>
                                                    {file && (
                                                        <div className="alert alert-success mt-2 py-2 d-flex justify-content-between align-items-center">
                                                            <span><i className="ri-check-line me-2"></i>File selected: {file.name}</span>
                                                            <Button variant="link" size="sm" className="text-danger p-0" onClick={() => setFile(null)}>
                                                                <i className="ri-close-line me-1"></i> Remove
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {userSelectionMode === 'manual' && (
                                        <Col md={12} className="mb-2">
                                            <div className="card border">
                                                <div className="card-body">
                                                    <Form.Label className="fw-semibold">
                                                        <i className="ri-user-search-line me-2"></i>Select Users Manually
                                                    </Form.Label>
                                                    <Select
                                                        isMulti
                                                        options={usersInfo}
                                                        value={selectedUsers}
                                                        onChange={(u) => setSelectedUsers(u || [])}
                                                        onInputChange={(q, meta) => { if (meta.action === 'input-change') searchUsers(q); }}
                                                        filterOption={() => true}
                                                        placeholder="Search and select users..."
                                                        closeMenuOnSelect={false}
                                                        styles={selectStyles}
                                                        theme={selectTheme}
                                                        menuPortalTarget={document.body}
                                                    />
                                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                                        <small className="text-muted">{selectedUsers.length} user(s) selected</small>
                                                        {selectedUsers.length > 0 && (
                                                            <Button variant="link" size="sm" onClick={() => setSelectedUsers([])} className="text-danger p-0">
                                                                <i className="ri-close-line me-1"></i> Clear All
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {userSelectionMode === 'filter' && (
                                        <Col md={12} className="mb-2">
                                            <div className="card border">
                                                <div className="card-header bg-light py-2">
                                                    <h6 className="mb-0">
                                                        <i className="ri-filter-3-line me-2"></i>
                                                        Filter Users (matched at allocation time by the backend)
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <Row className="g-3">
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Outlet</Form.Label>
                                                            <Select
                                                                options={userFilterOutletOptions}
                                                                value={userFilterOutletOptions.find((o) => o.value === userFilters.outlet) || null}
                                                                onChange={(opt) => handleUserFilterChange('outlet', opt ? opt.value : '')}
                                                                isClearable
                                                                placeholder="Select outlet"
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Start Date</Form.Label>
                                                            <DatePicker
                                                                selected={userFilters.startDate}
                                                                onChange={(d) => handleUserFilterChange('startDate', d)}
                                                                dateFormat="dd/MM/yyyy"
                                                                placeholderText="Start Date"
                                                                className="form-control"
                                                                maxDate={userFilters.endDate || new Date()}
                                                                popperClassName="datepicker-popper"
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">End Date</Form.Label>
                                                            <DatePicker
                                                                selected={userFilters.endDate}
                                                                onChange={(d) => handleUserFilterChange('endDate', d)}
                                                                dateFormat="dd/MM/yyyy"
                                                                placeholderText="End Date"
                                                                className="form-control"
                                                                minDate={userFilters.startDate}
                                                                maxDate={new Date()}
                                                                popperClassName="datepicker-popper"
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">User Type</Form.Label>
                                                            <Form.Select
                                                                value={userFilters.filter}
                                                                onChange={(e) => handleUserFilterChange('filter', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="JOINING_COUPON_ONE_ORDER">Joining Coupon Users (1 order)</option>
                                                                <option value="COUPON_ORDERS">Coupon Users</option>
                                                                <option value="NO_COUPON_ORDERS">Non-Coupon Users</option>
                                                                <option value="REFERRING_USERS">Referring Users</option>
                                                                <option value="REFERRED_USERS">Referred Users</option>
                                                                <option value="REPEATED_USERS_DAYS">Repeated Users (By Days)</option>
                                                            </Form.Select>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Min Price</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min={0}
                                                                placeholder="Enter Min Price"
                                                                value={userFilters.minPrice}
                                                                onChange={(e) => handleUserFilterChange('minPrice', e.target.value)}
                                                                disabled={isUserFilterSpecialTypeSelected}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Max Price</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min={0}
                                                                placeholder="Enter Max Price"
                                                                value={userFilters.maxPrice}
                                                                onChange={(e) => handleUserFilterChange('maxPrice', e.target.value)}
                                                                disabled={isUserFilterSpecialTypeSelected}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Category</Form.Label>
                                                            <Select
                                                                options={categoriesData}
                                                                value={categoriesData.find((o) => o.value === userFilters.category) || null}
                                                                onChange={(opt) => handleUserFilterChange('category', opt ? opt.value : '')}
                                                                isClearable
                                                                isDisabled={isUserFilterSpecialTypeSelected}
                                                                placeholder="Select category"
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Product</Form.Label>
                                                            <Select
                                                                options={userFilterProductOptions}
                                                                value={userFilterProductOptions.find((o) => o.value === userFilters.product) || null}
                                                                onChange={(opt) => handleUserFilterChange('product', opt ? opt.value : '')}
                                                                isClearable
                                                                isDisabled={isUserFilterSpecialTypeSelected}
                                                                placeholder="Select product"
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Preferred Taste</Form.Label>
                                                            <Form.Select
                                                                value={userFilters.foodType}
                                                                onChange={(e) => handleUserFilterChange('foodType', e.target.value)}
                                                                disabled={isUserFilterSpecialTypeSelected}
                                                            >
                                                                <option value="">Select</option>
                                                                <option value="FOOD">Food</option>
                                                                <option value="BEVERAGE">Beverage</option>
                                                                <option value="COMBOS">Combos</option>
                                                                <option value="DESSERTS">Deserts</option>
                                                                <option value="EXTRAS">Extras</option>
                                                            </Form.Select>
                                                        </Col>
                                                        <Col md={3}>
                                                            <Form.Label className="fw-semibold">Search (name / mobile)</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Search by name or mobile"
                                                                value={userFilters.search}
                                                                onChange={(e) => handleUserFilterChange('search', e.target.value)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <small className="text-muted mt-2 d-block">
                                                        <i className="ri-information-line me-1"></i>
                                                        These filters are sent to the backend, which will automatically match and allocate the coupon to all eligible users. No manual user selection is needed.
                                                    </small>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        )}

                        {/* ---------------- Step 2: coupon type ---------------- */}
                        <div className="mb-2">
                            <StepTitle n={step(2)} icon="ri-coupon-3-line" title="Coupon Type" />
                            {isEdit && (
                                <small className="text-muted d-block mb-2">
                                    <i className="ri-lock-line me-1"></i>Coupon type can't be changed after creation
                                </small>
                            )}
                            <Card className="border">
                                <Card.Body>
                                    <Row className="g-3">
                                        {COUPON_TYPES.map(({ id, label, field, icon }) => (
                                            <Col md={4} lg={2} key={id}>
                                                <div className="form-check card-option">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`uca-${id}`}
                                                        label={
                                                            <div className="d-flex flex-column align-items-center">
                                                                <i className={`${icon} fs-4 mb-2 ${couponData[field] ? 'text-primary' : 'text-muted'}`}></i>
                                                                <span className="small">{label}</span>
                                                            </div>
                                                        }
                                                        checked={!!couponData[field]}
                                                        onChange={handleCheckboxChange(field)}
                                                        disabled={isEdit}
                                                        className={`custom-checkbox fw-semibold ${couponData[field] ? 'active' : ''}`}
                                                    />
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* ---------------- Step 3: details ---------------- */}
                        <div className="mb-2">
                            <StepTitle n={step(3)} icon="ri-information-line" title="Coupon Details" />
                            <Row className="g-3">
                                <Col md={3}>
                                    <Floating label="Title" required>
                                        <Form.Control placeholder="Title" name="title" value={couponData.title || ''} onChange={handleInputChange} required />
                                    </Floating>
                                </Col>
                                <Col md={3}>
                                    <Floating label="Coupon Code" required>
                                        <Form.Control placeholder="Coupon Code" name="couponCode" value={couponData.couponCode || ''} onChange={handleInputChange} required />
                                    </Floating>
                                </Col>
                                <Col md={3}>
                                    <Floating label="Usage Type" required>
                                        <Form.Select name="usageType" value={couponData.usageType} onChange={handleInputChange} required>
                                            <option value="">Select Usage Type</option>
                                            <option value="ONE-TIME">ONE-TIME</option>
                                            <option value="MULTIPLE">MULTIPLE</option>
                                        </Form.Select>
                                    </Floating>
                                </Col>
                                <Col md={3}>
                                    <Floating label="Daily Usage Limit">
                                        <Form.Control min={0} placeholder="Daily Usage Limit" name="dailyUsageLimit" value={couponData.dailyUsageLimit || ''} onChange={handleInputChange} />
                                    </Floating>
                                </Col>

                                <Col md={3}>
                                    <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                                    <DatePicker
                                        selected={fromDate}
                                        onChange={(d) => d && setFromDate(d)}
                                        placeholderText="Start Date"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        minDate={isEdit ? undefined : new Date()}
                                        required
                                        popperClassName="datepicker-popper"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                                    <DatePicker
                                        selected={toDate}
                                        onChange={(d) => d && setToDate(d)}
                                        placeholderText="End Date"
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        minDate={fromDate || (isEdit ? undefined : new Date())}
                                        required
                                        popperClassName="datepicker-popper"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Floating label="Start Time">
                                        <Form.Control type="time" name="startTime" value={couponData.startTime} onChange={handleInputChange} />
                                    </Floating>
                                </Col>
                                <Col md={3}>
                                    <Floating label="End Time">
                                        <Form.Control type="time" name="endTime" value={couponData.endTime} onChange={handleInputChange} />
                                    </Floating>
                                </Col>

                                {couponData.isFreebieCoupon && (
                                    <>
                                        <Col md={6}>
                                            <Form.Label className="mb-1">Freebie Product <span className="text-danger">*</span></Form.Label>
                                            <Select
                                                options={freebieProductsOptions}
                                                value={freebieProductsOptions.find((o) => o.value === couponData.freebieProduct) || null}
                                                onChange={(opt) => setCouponData((prev) => ({ ...prev, freebieProduct: opt ? opt.value : '' }))}
                                                isClearable
                                                placeholder="Select Freebie Product"
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Floating label="Quantity" required>
                                                <Form.Control type="number" min="1" name="freebieQuantity" value={couponData.freebieQuantity || ''} onChange={handleInputChange} placeholder="Quantity" />
                                            </Floating>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </div>

                        {/* ---------------- Step 4: advanced ---------------- */}
                        <div className="mb-2">
                            <StepTitle n={step(4)} icon="ri-settings-3-line" title="Advanced Configuration" />
                            <Row className="g-3">
                                {!noDiscountFields && (
                                    <>
                                        <Col md={3}>
                                            <Floating label="Discount %">
                                                <Form.Control
                                                    min={0}
                                                    max={100}
                                                    type="number"
                                                    disabled={!!(couponData.discountAmount || couponData.discountPrice)}
                                                    placeholder="Discount Percent"
                                                    name="discountPercent"
                                                    value={couponData.discountPercent || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Floating>
                                        </Col>
                                        <Col md={3}>
                                            <Floating label="Max Discount">
                                                <Form.Control
                                                    min={0}
                                                    type="number"
                                                    disabled={!!(couponData.discountAmount || couponData.discountPrice)}
                                                    placeholder="Max. Dis. Amount"
                                                    name="maxDiscountAmount"
                                                    value={couponData.maxDiscountAmount}
                                                    onChange={handleInputChange}
                                                />
                                            </Floating>
                                        </Col>
                                        <Col md={3}>
                                            <Floating label="Flat Discount">
                                                <Form.Control
                                                    min={0}
                                                    step={0.01}
                                                    type="number"
                                                    disabled={!!(couponData.discountPercent || couponData.discountPrice || universalCoupon)}
                                                    placeholder="Discount Amount"
                                                    name="discountAmount"
                                                    value={couponData.discountAmount || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Floating>
                                        </Col>
                                        <Col md={3}>
                                            <Floating label="Discount Price">
                                                <Form.Control
                                                    min={0}
                                                    step={0.01}
                                                    type="number"
                                                    disabled={!!(couponData.discountAmount || couponData.discountPercent || universalCoupon)}
                                                    placeholder="Discount Price"
                                                    name="discountPrice"
                                                    value={couponData.discountPrice || ''}
                                                    onChange={handleInputChange}
                                                />
                                            </Floating>
                                        </Col>
                                    </>
                                )}

                                {couponData.isSizeUpgradeCoupon && (
                                    <Col md={4}>
                                        <Floating label="Size Upgrade Addon" required>
                                            <Form.Select
                                                name="newSizeUpgradeAddon"
                                                value={couponData.newSizeUpgradeAddon}
                                                onChange={(e) => setCouponData((prev) => ({ ...prev, newSizeUpgradeAddon: e.target.value }))}
                                                required
                                            >
                                                <option value="">Select Size Upgrade Addon</option>
                                                {newSizeAddonsList.map((addon) => (
                                                    <option key={addon._id} value={addon._id}>
                                                        {addon.title} [{addon.identifier}]
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Floating>
                                    </Col>
                                )}

                                <Col md={3}>
                                    <Floating label="Order Type">
                                        <Form.Select name="orderType" value={couponData.orderType || 'ALL'} onChange={handleInputChange}>
                                            <option value="ALL">ALL</option>
                                            <option value="DELIVERY">DELIVERY</option>
                                            <option value="PICKUP">PICK-UP</option>
                                        </Form.Select>
                                    </Floating>
                                </Col>
                                <Col md={3}>
                                    <Floating label="Min. Order Amount">
                                        <Form.Control min={0} type="number" placeholder="Min. Order Amount" name="minOrderAmount" value={couponData.minOrderAmount} onChange={handleInputChange} />
                                    </Floating>
                                </Col>
                                <Col md={4}>
                                    <Form.Label className="fw-semibold">Applicable Day(s)</Form.Label>
                                    <Select
                                        isMulti
                                        components={animatedComponents}
                                        options={dayOfWeekOptions}
                                        value={selectedDaysOfWeek}
                                        onChange={(s) => setSelectedDaysOfWeek(s || [])}
                                        placeholder="Select day(s) (leave empty for any day)"
                                        closeMenuOnSelect={false}
                                        styles={selectStyles}
                                        theme={selectTheme}
                                        menuPortalTarget={document.body}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Floating label="Description">
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            placeholder="Description"
                                            name="description"
                                            value={couponData.description || ''}
                                            onChange={handleInputChange}
                                            style={{ minHeight: '100px' }}
                                        />
                                    </Floating>
                                </Col>
                            </Row>
                        </div>

                        {/* ---------------- filters & restrictions ---------------- */}
                        <div className="mb-2">
                            <h6 className="fw-bold mb-3">
                                <i className="ri-filter-2-line me-2 text-primary"></i>
                                Advanced Filters & Restrictions
                            </h6>
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-list-check me-2"></i>Product & Category Filters</h6>
                                        </div>
                                        <div className="card-body">
                                            <MultiSelectComponent
                                                label="Applicable Categories"
                                                placeholder="Select categories"
                                                value={selections.selectedApplicableCategories || []}
                                                onChange={(v) => handleChange('selectedApplicableCategories', v)}
                                                options={categoriesData}
                                                isDisabled={!!selections.selectedApplicableProducts?.length || couponData.isComboCategoryCoupon || couponData.isFreebieCoupon}
                                                helpText="Products from these categories will be eligible"
                                            />
                                            <MultiSelectComponent
                                                label="Applicable Products"
                                                placeholder="Select specific products"
                                                value={selections.selectedApplicableProducts || []}
                                                onChange={(v) => handleChange('selectedApplicableProducts', v)}
                                                options={productsInfo}
                                                isDisabled={!!selections.selectedApplicableCategories?.length || couponData.isBogoCoupon || couponData.isComboCategoryCoupon || couponData.isFreebieCoupon || universalCoupon}
                                                helpText="Only these specific products will be eligible"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                {couponData.isComboCategoryCoupon && (
                                    <Col md={6}>
                                        <div className="card border h-100">
                                            <div className="card-header bg-light py-2">
                                                <h6 className="mb-0"><i className="ri-links-line me-2"></i>Combo Categories (For Combo Coupons)</h6>
                                            </div>
                                            <div className="card-body">
                                                <MultiSelectComponent
                                                    label="Primary Combo Categories"
                                                    placeholder="Select primary categories"
                                                    value={selections.primaryComboCategories || []}
                                                    onChange={(v) => handleChange('primaryComboCategories', v)}
                                                    options={categoriesData.filter((c) => !selections.secondaryComboCategories?.some((s) => s.value === c.value))}
                                                    isDisabled={comboLocked}
                                                    helpText="Primary categories for combo discounts"
                                                />
                                                <MultiSelectComponent
                                                    label="Secondary Combo Categories"
                                                    placeholder="Select secondary categories"
                                                    value={selections.secondaryComboCategories || []}
                                                    onChange={(v) => handleChange('secondaryComboCategories', v)}
                                                    options={categoriesData.filter((c) => !selections.primaryComboCategories?.some((p) => p.value === c.value))}
                                                    isDisabled={comboLocked}
                                                    helpText="Secondary categories that combine with primary"
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                )}

                                <Col md={6}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-forbid-line me-2"></i>Exclusions</h6>
                                        </div>
                                        <div className="card-body">
                                            <MultiSelectComponent
                                                label="Include Outlets"
                                                placeholder="Select outlets to include"
                                                options={outletData}
                                                value={selections.selectedIncludedOutlets || []}
                                                onChange={(v) => handleChange('selectedIncludedOutlets', v)}
                                                isDisabled={!!selections.selectedExcludedOutlets?.length}
                                            />
                                            <MultiSelectComponent
                                                label="Exclude Outlets"
                                                placeholder="Select outlets to exclude"
                                                value={selections.selectedExcludedOutlets || []}
                                                onChange={(v) => handleChange('selectedExcludedOutlets', v)}
                                                options={outletData}
                                                isDisabled={!!selections.selectedIncludedOutlets?.length}
                                            />
                                            <MultiSelectComponent
                                                label="Excluded Products"
                                                placeholder="Select products to exclude"
                                                value={selections.selectedExcludedProducts || []}
                                                onChange={(v) => handleChange('selectedExcludedProducts', v)}
                                                options={productsInfo}
                                                helpText="These products will NOT be eligible for the coupon"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-node-tree me-2"></i>Dependencies</h6>
                                        </div>
                                        <div className="card-body">
                                            <MultiSelectComponent
                                                label="Dependent Products"
                                                placeholder="Select dependent products"
                                                value={selections.selectedDependentProducts || []}
                                                onChange={(v) => handleChange('selectedDependentProducts', v)}
                                                options={productsInfo}
                                                customStyles={selectStyles}
                                                isDisabled={dependencyLocked}
                                                helpText="Products that must be in cart for coupon to apply"
                                            />
                                            <MultiSelectComponent
                                                label="Dependent Categories"
                                                placeholder="Select dependent categories"
                                                value={selections.selectedDependentCategories || []}
                                                onChange={(v) => handleChange('selectedDependentCategories', v)}
                                                options={categoriesData}
                                                isDisabled={dependencyLocked}
                                                helpText="Categories that must be in cart for coupon to apply"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-store-line me-2"></i>User Level Filters & Addon Limit</h6>
                                        </div>
                                        <div className="card-body">
                                            <MultiSelectComponent
                                                label="Applicable Levels"
                                                placeholder="Select user levels"
                                                value={selections.selectedApplicableLevels || []}
                                                onChange={(v) => handleChange('selectedApplicableLevels', v)}
                                                options={levelsData}
                                                helpText="Only users at these levels can use this coupon"
                                            />
                                            <Floating label="Max Addons Allowed" hint="Maximum number of addons allowed per item (0 = no limit)">
                                                <Form.Control
                                                    min={0}
                                                    placeholder="Maximum Addons Allowed"
                                                    type="number"
                                                    name="maxAddonsAllowed"
                                                    value={couponData.maxAddonsAllowed || ''}
                                                    onChange={handleInputChange}
                                                    disabled={couponData.isFreebieCoupon}
                                                />
                                            </Floating>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-list-settings-line me-2"></i>Addon Exclusions</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <Form.Label className="fw-semibold"><i className="ri-list-check me-2"></i>Excluding Addon Groups</Form.Label>
                                                <Select
                                                    components={animatedComponents}
                                                    isMulti
                                                    options={allAddonList.map((a) => ({ value: a._id, label: a.title, icon: a.icon || 'ri-list-settings' }))}
                                                    value={selectedAddonGroups}
                                                    onChange={handleSelectAddonGroups}
                                                    placeholder="Select addon groups to exclude..."
                                                    isDisabled={couponData.isFreebieCoupon}
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    formatOptionLabel={({ label, icon }) => (
                                                        <div className="d-flex align-items-center">
                                                            <i className={`${icon || 'ri-list-settings'} me-2`}></i>
                                                            <span>{label}</span>
                                                        </div>
                                                    )}
                                                />
                                                <small className="text-muted mt-1 d-block">Select addon groups whose values should be excluded</small>
                                            </div>
                                            <div className="mb-3">
                                                <Form.Label className="fw-semibold"><i className="ri-list-settings-line me-2"></i>Excluding Addon Values</Form.Label>
                                                <Select
                                                    components={animatedComponents}
                                                    isMulti
                                                    options={filteredAddonValues}
                                                    value={selectedAddonValues}
                                                    onChange={handleAddonValueChange}
                                                    placeholder={selectedAddonGroups.length === 0 ? 'Select addon groups first' : 'Select specific addon values to exclude'}
                                                    getOptionLabel={(o) => o.label}
                                                    getOptionValue={(o) => `${o.value.addonId}-${o.value.addonValueId}`}
                                                    isDisabled={selectedAddonGroups.length === 0 || couponData.isFreebieCoupon}
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                />
                                                <small className="text-muted mt-1 d-block">
                                                    {selectedAddonValues.length > 0 ? `${selectedAddonValues.length} addon value(s) selected` : 'No addon values excluded'}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={4}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-number-1 me-2"></i>Quantity Restrictions</h6>
                                        </div>
                                        <div className="card-body">
                                            <Floating label="Applicable Quantity Type" hint="Restricts coupon to single or multiple items">
                                                <Form.Select
                                                    name="applicableQuantityType"
                                                    value={couponData.applicableQuantityType || ''}
                                                    onChange={handleInputChange}
                                                    disabled={!!(
                                                        couponData.discountPercent ||
                                                        couponData.discountAmount ||
                                                        couponData.isBogoCoupon ||
                                                        couponData.isSizeUpgradeCoupon ||
                                                        couponData.isMrpDiscount ||
                                                        couponData.isFreebieCoupon
                                                    )}
                                                >
                                                    <option value="">Select Quantity Type</option>
                                                    <option value="SINGLE">SINGLE (One item only)</option>
                                                    {!couponData.isComboCategoryCoupon && <option value="MULTIPLE">MULTIPLE (Multiple items)</option>}
                                                </Form.Select>
                                            </Floating>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={4}>
                                    <div className="card border h-100">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="ri-eye-off-line me-2"></i>Visibility</h6>
                                        </div>
                                        <div className="card-body">
                                            <Floating label="Coupon Visibility" hint="Hide coupon from user view if needed">
                                                <Form.Select name="isHidden" value={couponData.isHidden || 'false'} onChange={handleInputChange}>
                                                    <option value="false">Visible to Users</option>
                                                    <option value="true">Hidden from Users</option>
                                                </Form.Select>
                                            </Floating>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* ---------------- terms ---------------- */}
                        <div className="mb-2">
                            <h6 className="fw-bold mb-3">
                                <i className="ri-file-text-line me-2 text-primary"></i>
                                Terms & Conditions
                            </h6>
                            <div className="card border">
                                <div className="card-body">
                                    <div className="d-flex mb-3">
                                        <Form.Control
                                            placeholder="Enter term..."
                                            value={currentTerm}
                                            onChange={(e) => setCurrentTerm(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTerm(); } }}
                                            className="me-2"
                                        />
                                        <Button variant="primary" onClick={addTerm} disabled={!currentTerm.trim()}>
                                            <i className="ri-add-line me-1"></i> Add
                                        </Button>
                                    </div>
                                    {couponData.termsAndConditions?.length > 0 && (
                                        <div className="terms-list">
                                            {couponData.termsAndConditions.map((term, index) => (
                                                <div key={index} className="alert alert-light d-flex justify-content-between align-items-center mb-2">
                                                    <span>{term}</span>
                                                    <Button variant="outline-danger" size="sm" onClick={() => removeTerm(index)}>
                                                        <i className="ri-close-line"></i>
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="outline-secondary" onClick={onHide} disabled={isLoading}>
                        <i className="ri-close-line me-1"></i> Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {isEdit ? 'Saving...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <i className="ri-check-line me-1"></i> {isEdit ? 'Save Changes' : 'Create Coupon'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

/* ------------------------------ main component ------------------------------ */
const STATUS_OPTIONS = [
    { value: '', label: 'All Status' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
];
const SORT_OPTIONS = [
    { value: 'ASC', label: 'Title: A → Z' },
    { value: 'DESC', label: 'Title: Z → A' },
];

const UserCouponAllocation = () => {
    const [, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');
    const { loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem('email');
    const [couponAccess] = useState([]);
    const can = (perm) => couponAccess.includes(perm) || email === 'contact@xpansecafe.com';

    const accessToken = localStorage.getItem('authToken');
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeCoupon, setActiveCoupon] = useState(null);
    const [form, setForm] = useState({ show: false, mode: 'create', data: null });

    // list query state (sent to the API: page, limit, status, search, sortBy)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [status, setStatus] = useState('');
    const [sortBy, setSortBy] = useState('ASC');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1 });
    const requestId = useRef(0);

    // debounce search -> API, reset to page 1 when the term changes
    useEffect(() => {
        const t = setTimeout(() => {
            const q = searchInput.trim();
            setSearch((prev) => {
                if (prev !== q) setPage(1);
                return q;
            });
        }, 500);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchCoupons = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit, sortBy });
            if (status) params.append('status', status);
            if (search) params.append('search', search);

            const res = await axiosInstance.get(`user-coupon-allocation/couponlist?${params.toString()}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (id !== requestId.current) return; // stale response
            if (res?.data?.status === true) {
                const d = res.data.data || {};
                const list = d.allocatedCoupons || d.coupons || [];
                const p = d.pagination || {};
                const totalRecords = p.totalRecords ?? d.totalRecords ?? d.total ?? list.length;
                const totalPages = Math.max(1, p.totalPages ?? d.totalPages ?? Math.ceil(totalRecords / limit));
                setCoupons(list);
                setPagination({ totalRecords, totalPages });
                if (page > totalPages) setPage(totalPages);
            }
        } catch (e) {
            if (id === requestId.current) notify.error(getErr(e, 'Failed to fetch coupons'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [accessToken, page, limit, status, search, sortBy]);

    useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

    const hasFilters = !!(searchInput || status || sortBy !== 'ASC');
    const clearFilters = () => {
        setSearchInput('');
        setSearch('');
        setStatus('');
        setSortBy('ASC');
        setPage(1);
    };

    const openCreate = () => setForm({ show: true, mode: 'create', data: null });
    const openEdit = (details) => setForm({ show: true, mode: 'edit', data: details });
    const closeForm = () => setForm((f) => ({ ...f, show: false }));

    const handleFormDone = (patch) => {
        if (form.mode === 'edit' && patch) {
            setActiveCoupon((prev) => (prev ? { ...prev, ...patch } : prev));
        }
        fetchCoupons();
    };

    // The view modal steps aside while the edit form is open, then reopens (and refetches) on close
    const viewModalVisible = !!activeCoupon && !(form.show && form.mode === 'edit');

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    <div className="uca-header">
                        <div>
                            <h4 className="fw-bold mb-1">
                                <i className="ri-coupon-3-line me-2 text-primary"></i>
                                User Coupon Allocation
                            </h4>
                            <small className="text-muted">
                                {loading ? 'Loading…' : `${pagination.totalRecords} coupon(s)`}
                            </small>
                        </div>
                        <div className="uca-actions">
                            <Button variant="light" className="border" onClick={fetchCoupons} disabled={loading} title="Refresh">
                                <i className={`ri-refresh-line ${loading ? 'uca-spin' : ''}`}></i>
                            </Button>
                            {can('UserCouponAllocation:add') && (
                                <Button variant="primary" className="fw-semibold" onClick={openCreate}>
                                    <i className="ri-add-line me-1"></i> Allocate New Coupon
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ---------- filters ---------- */}
                    <div className="uca-filters">
                        <div className="uca-search">
                            <i className="ri-search-line"></i>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by title or code..."
                            />
                            {searchInput && (
                                <button type="button" onClick={() => setSearchInput('')}><i className="ri-close-line"></i></button>
                            )}
                        </div>

                        <Form.Select
                            className="uca-filter-select"
                            value={status}
                            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                            aria-label="Filter by status"
                        >
                            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>

                        <Form.Select
                            className="uca-filter-select"
                            value={sortBy}
                            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                            aria-label="Sort"
                        >
                            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>

                        {hasFilters && (
                            <Button variant="outline-secondary" className="uca-clear" onClick={clearFilters}>
                                <i className="ri-filter-off-line me-1"></i> Clear
                            </Button>
                        )}
                    </div>

                    {loading && coupons.length === 0 ? (
                        <div className="uca-grid">{Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}</div>
                    ) : coupons.length === 0 ? (
                        <div className="uca-empty big">
                            <i className="ri-coupon-line"></i>
                            <h5>No coupons found</h5>
                            <p>{hasFilters ? 'Try different filters or search term' : 'Allocate a new coupon to get started'}</p>
                        </div>
                    ) : (
                        <div
                            key={`${page}-${limit}-${status}-${sortBy}-${search}`}
                            className={`uca-grid ${loading ? 'uca-dim' : ''}`}
                        >
                            {coupons.map((c, i) => <CouponCard key={c._id} coupon={c} index={i} onOpen={setActiveCoupon} />)}
                        </div>
                    )}

                    {pagination.totalRecords > 0 && (
                        <Pager
                            page={page}
                            limit={limit}
                            totalPages={pagination.totalPages}
                            totalRecords={pagination.totalRecords}
                            onPage={setPage}
                            onLimit={(n) => { setLimit(n); setPage(1); }}
                        />
                    )}
                </div>

                <CouponModal
                    show={viewModalVisible}
                    coupon={activeCoupon}
                    onHide={() => setActiveCoupon(null)}
                    canAdd={can('UserCouponAllocation:update')}
                    canDelete={can('UserCouponAllocation:delete')}
                    canEdit={can('UserCouponAllocation:update')}
                    onEdit={openEdit}
                />

                <CouponFormModal
                    show={form.show}
                    mode={form.mode}
                    initialCoupon={form.data}
                    onHide={closeForm}
                    onDone={handleFormDone}
                />
            </div>

            <style>{`
                .datepicker-popper{z-index:9999 !important}
                .react-datepicker-wrapper{width:100%}

                .uca-header{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:16px}
                .uca-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
                .uca-filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:20px}
                .uca-filter-select{width:auto;min-width:150px;height:38px;border-radius:10px;font-size:14px}
                .uca-clear{height:38px;border-radius:10px;display:inline-flex;align-items:center}
                .uca-dim{opacity:.55;transition:opacity .2s;pointer-events:none}
                .uca-search{position:relative;display:flex;align-items:center;background:#fff;border:1px solid #dee2e6;border-radius:10px;padding:0 10px;height:38px;min-width:260px;transition:.2s}
                .uca-search.sm{height:31px;min-width:250px;border-radius:8px}
                .uca-search:focus-within{border-color:#6366f1;box-shadow:0 0 0 3px rgba(99,102,241,.15)}
                .uca-search i{color:#94a3b8}
                .uca-search input{border:0;outline:0;background:transparent;flex:1;padding:0 8px;font-size:14px}
                .uca-search.sm input{font-size:13px}
                .uca-search button{border:0;background:transparent;color:#94a3b8;padding:0}
                .uca-spin{animation:uca-rot 1s linear infinite;display:inline-block}
                @keyframes uca-rot{to{transform:rotate(360deg)}}

                .uca-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:18px}
                .uca-card{position:relative;background:#fff;border:1px solid #eef0f4;border-radius:16px;padding:18px;cursor:pointer;overflow:hidden;transition:transform .2s,box-shadow .2s,border-color .2s;outline:none}
                .uca-card::before{content:'';position:absolute;inset:0 0 auto 0;height:4px;background:var(--uca-accent)}
                .uca-card:hover,.uca-card:focus-visible{transform:translateY(-4px);box-shadow:0 14px 30px rgba(15,23,42,.10);border-color:#dfe3ec}
                .uca-card-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
                .uca-avatar{width:44px;height:44px;border-radius:12px;background:var(--uca-accent);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:18px}
                .uca-copy{border:0;background:#f4f5f9;color:#64748b;width:32px;height:32px;border-radius:8px;transition:.2s}
                .uca-copy:hover{background:#e8eaf3;color:#4f46e5}
                .uca-card-title{font-weight:700;margin:0 0 10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                .uca-code{display:inline-flex;align-items:center;gap:6px;background:#f5f3ff;color:#5b21b6;border:1px dashed #c4b5fd;border-radius:8px;padding:4px 10px;font-size:12.5px;font-weight:600;letter-spacing:.5px;max-width:100%}
                .uca-code span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
                .uca-card-foot{display:flex;justify-content:space-between;align-items:center;margin-top:16px;padding-top:12px;border-top:1px solid #f1f2f6;font-size:12.5px;color:#64748b;font-weight:500}
                .uca-card-foot i{transition:transform .2s}
                .uca-card:hover .uca-card-foot{color:#4f46e5}
                .uca-card:hover .uca-card-foot i{transform:translateX(4px)}

                /* active vs inactive cards */
                .uca-status{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:3px 10px;font-size:11px;font-weight:700;letter-spacing:.3px;text-transform:uppercase}
                .uca-status-dot{width:7px;height:7px;border-radius:50%;background:currentColor}
                .uca-status.active{background:#e7f8ef;color:#15803d}
                .uca-status.active .uca-status-dot{animation:uca-pulse 1.8s ease-out infinite}
                .uca-status.inactive{background:#f1f5f9;color:#64748b}
                @keyframes uca-pulse{0%{box-shadow:0 0 0 0 rgba(34,197,94,.55)}70%{box-shadow:0 0 0 6px rgba(34,197,94,0)}100%{box-shadow:0 0 0 0 rgba(34,197,94,0)}}

                .uca-card.is-active{border-color:#d9f2e3}
                .uca-card.is-inactive{background:#f8fafc;border-color:#e5e7eb;border-style:dashed}
                .uca-card.is-inactive .uca-avatar{filter:grayscale(1);opacity:.75}
                .uca-card.is-inactive .uca-card-title{color:#64748b}
                .uca-card.is-inactive .uca-code{background:#f1f5f9;color:#64748b;border-color:#cbd5e1}
                .uca-card.is-inactive .uca-code span{text-decoration:line-through;text-decoration-color:#94a3b8}
                .uca-card.is-inactive:hover{box-shadow:0 10px 22px rgba(15,23,42,.06)}

                /* framer-motion owns transform on cards */
                .uca-card{transition:box-shadow .25s,border-color .25s;transform-style:preserve-3d;will-change:transform}
                .uca-card:hover,.uca-card:focus-visible{transform:none}
                .uca-card.is-active:hover{box-shadow:0 18px 38px rgba(34,197,94,.16),0 6px 14px rgba(15,23,42,.08)}
                .uca-card-foot{border-top:2px dashed #e5e7eb}
                .uca-card-foot i,.uca-card:hover .uca-card-foot i{transition:none;transform:none}
                .uca-card > *:not(.uca-glare):not(.uca-shimmer):not(.uca-stamp){position:relative;z-index:1}
                .uca-glare{position:absolute;inset:0;z-index:0;opacity:0;pointer-events:none;mix-blend-mode:soft-light;transition:opacity .25s}
                .uca-card:hover .uca-glare{opacity:1}
                .uca-shimmer{position:absolute;top:0;bottom:0;left:0;width:34%;z-index:0;pointer-events:none;transform:skewX(-20deg);background:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.7),rgba(255,255,255,0))}
                .uca-stamp{position:absolute;right:14px;bottom:62px;z-index:2;pointer-events:none;border:2px solid #94a3b8;color:#64748b;border-radius:8px;padding:2px 10px;font-size:13px;font-weight:800;letter-spacing:2px;text-transform:uppercase;background:rgba(248,250,252,.85)}
                .uca-card.is-inactive .uca-card-title{max-width:70%}

                .uca-skeleton{cursor:default;pointer-events:none}
                .uca-sk{background:linear-gradient(90deg,#f1f2f6 25%,#e7e9f0 37%,#f1f2f6 63%);background-size:400% 100%;animation:uca-sh 1.3s ease infinite;border-radius:8px}
                .uca-sk-avatar{width:44px;height:44px;border-radius:12px;margin-bottom:16px}
                .uca-sk-line{height:12px;margin-bottom:10px}
                @keyframes uca-sh{0%{background-position:100% 50%}100%{background-position:0 50%}}

                .uca-empty{text-align:center;color:#94a3b8;padding:36px 0}
                .uca-empty i{font-size:38px;display:block;margin-bottom:6px}
                .uca-empty.big{padding:80px 0}
                .uca-empty.big i{font-size:56px}
                .uca-empty p{margin:0}

                .uca-modal .modal-content,.uca-form-modal .modal-content{border:0;border-radius:16px;overflow:hidden}

                /* chips (reused by the applicability expand panel) */
                .uca-chip{border-radius:8px;padding:3px 10px;font-size:12.5px;font-weight:600}
                .uca-chip.primary{background:#eef2ff;color:#4338ca}
                .uca-chip.success{background:#dcfce7;color:#15803d}
                .uca-chip.danger{background:#fee2e2;color:#b91c1c}
                .uca-chip.warning{background:#fef3c7;color:#b45309}
                .uca-chip.info{background:#e0f2fe;color:#0369a1}

                /* ---------- redesigned view modal (ucm) ---------- */
                .ucm .modal-content{border:0;border-radius:16px;overflow:hidden;box-shadow:0 24px 60px rgba(15,23,42,.25)}

                .ucm-head{display:flex;align-items:flex-start;gap:14px;padding:18px 22px 12px;background:#fff}
                .ucm-head-icon{width:52px;height:52px;flex-shrink:0;border-radius:12px;background:#eef0ff;color:#4f46e5;display:flex;align-items:center;justify-content:center;font-size:24px}
                .ucm-head-main{flex:1;min-width:0}
                .ucm-title-row{display:flex;align-items:center;gap:8px}
                .ucm-title{margin:0;font-size:20px;font-weight:700;letter-spacing:.1px;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                .ucm-icon-btn{border:0;background:transparent;color:#94a3b8;width:28px;height:28px;border-radius:8px;transition:.2s}
                .ucm-icon-btn:hover{background:#f1f5f9;color:#4f46e5}
                .ucm-chip-row{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin-top:4px}
                .ucm-code-label{color:#64748b;font-size:12.5px}
                .ucm-code-pill{border:1px solid #c7d2fe;background:#fff;color:#4f46e5;font-weight:700;border-radius:999px;padding:2px 12px;font-size:12.5px}
                .ucm-pill{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 10px;font-size:11px;font-weight:700;letter-spacing:.2px}
                .ucm-pill.green{background:#e7f8ef;color:#15803d}
                .ucm-pill.red{background:#fee2e2;color:#b91c1c}
                .ucm-pill.soft{background:#f1f5f9;color:#475569}
                .ucm-pill.discount{background:#e0e7ff;color:#3730a3;font-size:12.5px;padding:5px 12px;border-radius:9px}
                .ucm-pill.discount i{background:#4f46e5;color:#fff;border-radius:50%;width:17px;height:17px;display:inline-flex;align-items:center;justify-content:center;font-size:10px}
                .ucm-subtitle{margin-top:6px;color:#64748b;font-size:11.5px}
                .ucm-head-actions{display:flex;gap:8px;align-items:center;flex-shrink:0}

                .ucm-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;height:34px;padding:0 14px;border-radius:9px;font-size:12.5px;font-weight:600;border:1px solid transparent;transition:.2s;cursor:pointer}
                .ucm-btn.outline{background:#fff;border-color:#c7d2fe;color:#4338ca}
                .ucm-btn.outline:hover{background:#eef2ff}
                .ucm-btn.primary{background:#4f46e5;color:#fff}
                .ucm-btn.primary:hover{background:#4338ca}
                .ucm-btn.square{width:34px;padding:0;background:#fff;border-color:#e2e8f0;color:#334155;font-size:18px}
                .ucm-btn.square:hover{background:#f8fafc}
                .ucm-mini-btn{border:1px solid #e2e8f0;background:#fff;color:#4f46e5;width:26px;height:26px;border-radius:6px;font-size:13px;padding:0;display:inline-flex;align-items:center;justify-content:center}
                .ucm-mini-btn:hover{background:#eef2ff}

                .ucm-tabs{display:flex;gap:8px;padding:0 20px;border-top:1px solid #eef0f4;border-bottom:1px solid #eef0f4;background:#fff}
                .ucm-tabs button{border:0;background:transparent;padding:11px 10px;font-weight:600;font-size:12.5px;color:#64748b;border-bottom:3px solid transparent;display:inline-flex;align-items:center;gap:6px;transition:.2s}
                .ucm-tabs button:hover{color:#4f46e5}
                .ucm-tabs button.active{color:#4f46e5;border-bottom-color:#4f46e5}
                .ucm-count{background:#eef2ff;color:#4f46e5;border-radius:999px;padding:1px 10px;font-size:11.5px;font-weight:700;margin-left:4px}

                .ucm-body{padding:14px 18px;background:#f7f8fc;min-height:340px}
                .ucm-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:start}
                .ucm-grid .span-2{grid-column:1 / -1}
                .ucm-card{background:#fff;border:1px solid #e8ebf3;border-radius:12px;padding:14px 16px}
                .ucm-card-title{display:flex;align-items:center;gap:10px;margin:0 0 8px;font-size:13.5px;font-weight:700;color:#111827}
                .ucm-card-icon{width:24px;height:24px;border-radius:50%;background:#e0e7ff;color:#4f46e5;display:inline-flex;align-items:center;justify-content:center;font-size:12px}

                .ucm-items{display:grid;gap:0 18px}
                .ucm-items.two{grid-template-columns:1fr 1fr}
                .ucm-items.three{grid-template-columns:repeat(3,1fr)}
                .ucm-item{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid #f0f2f7;min-width:0;background:transparent;text-align:left}
                .ucm-items.two .ucm-item:nth-last-child(-n+2),.ucm-items.three .ucm-item:nth-last-child(-n+3){border-bottom:0}
                .ucm-item-icon{color:#8b93a7;font-size:17px;flex-shrink:0}
                .ucm-item-body{display:flex;flex-direction:column;min-width:0;flex:1}
                .ucm-label{font-size:9.5px;text-transform:uppercase;letter-spacing:.5px;color:#8b93a7;font-weight:600}
                .ucm-value{font-size:12.5px;font-weight:600;color:#111827;word-break:break-word}
                .ucm-dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex-shrink:0}
                .ucm-dot.green{background:#22c55e}.ucm-dot.red{background:#ef4444}.ucm-dot.grey{background:#94a3b8}

                .ucm-item-btn{border:0;border-bottom:1px solid #f0f2f7;width:100%;cursor:pointer;transition:.15s}
                .ucm-item-btn:hover .ucm-value{color:#4f46e5}
                .ucm-item-btn:focus-visible{outline:2px solid #6366f1;outline-offset:2px;border-radius:8px}
                .ucm-chevron{color:#94a3b8;font-size:17px;transition:transform .2s}
                .ucm-item-btn.open .ucm-chevron{transform:rotate(90deg);color:#4f46e5}
                .ucm-expand{margin-top:10px;background:#f8faff;border:1px solid #e0e7ff;border-radius:10px;padding:12px}
                .ucm-expand-head{display:flex;justify-content:space-between;align-items:center;font-size:12px;font-weight:700;color:#475569;margin-bottom:8px}
                .ucm-note{background:#eff6ff;color:#1d4ed8;border-radius:10px;padding:7px 11px;font-size:12px;display:flex;align-items:center;gap:6px}

                .ucm-alloc{display:grid;grid-template-columns:1.4fr 1fr 1fr;gap:18px;align-items:center}
                .ucm-alloc .ucm-item{border:0;padding:0}
                .ucm-alloc-status{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
                .ucm-alloc-status small{color:#64748b;font-size:11px}

                .ucm-text{margin:0;color:#475569;font-size:12.5px}
                .ucm-terms{margin:0;padding-left:20px;color:#475569;font-size:12.5px}
                .ucm-meta{display:flex;gap:18px;flex-wrap:wrap;color:#94a3b8;font-size:11.5px}
                .ucm-meta i{margin-right:4px}

                .ucm-foot{background:#fff;border-top:1px solid #eef0f4;padding:10px 18px;gap:10px;justify-content:flex-end}

                @media (max-width:992px){
                  .ucm-grid{grid-template-columns:1fr}
                  .ucm-items.three{grid-template-columns:1fr 1fr}
                  .ucm-items.three .ucm-item:nth-last-child(-n+3){border-bottom:1px solid #f0f2f7}
                  .ucm-alloc{grid-template-columns:1fr}
                }
                @media (max-width:576px){
                  .ucm-head{flex-wrap:wrap;padding:16px}
                  .ucm-head-actions{width:100%;justify-content:flex-end}
                  .ucm-items.two,.ucm-items.three{grid-template-columns:1fr}
                  .ucm-title{font-size:17px}
                }

                /* ---------- allocated users tab ---------- */
                .uca-users-bar{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:14px}
                .uca-add-box{background:#fff;border:1px solid #e0e7ff;border-radius:12px;padding:14px;margin-bottom:14px;animation:uca-in .2s ease}
                .uca-select-bar{display:flex;justify-content:space-between;align-items:center;gap:12px;background:#1e293b;color:#fff;border-radius:12px;padding:10px 14px;margin-bottom:12px;font-size:14px;animation:uca-in .2s ease}
                @keyframes uca-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
                .uca-table-wrap{position:relative;background:#fff;border:1px solid #eef0f4;border-radius:12px;overflow:hidden;transition:opacity .2s}
                .uca-table-wrap.loading{opacity:.55}
                .uca-table-loader{position:absolute;top:10px;right:14px;z-index:2}
                .uca-table thead th{background:#f8fafc;font-size:12px;text-transform:uppercase;letter-spacing:.4px;color:#64748b;border-bottom:1px solid #eef0f4;white-space:nowrap}
                .uca-table tbody tr{transition:background .15s}
                .uca-table tbody tr:hover{background:#f8faff}
                .uca-table tbody tr.selected{background:#eef2ff}
                .uca-user-avatar{width:32px;height:32px;border-radius:50%;color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0}
                .uca-row-del{border:0;background:transparent;color:#94a3b8;width:30px;height:30px;border-radius:8px;transition:.2s}
                .uca-row-del:hover{background:#fee2e2;color:#dc2626}
                .uca-pager{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-top:14px}

                .uca-step-title{display:flex;align-items:center;font-weight:700;margin-bottom:14px}
                .uca-step-badge{width:24px;height:24px;border-radius:50%;background:#4f46e5;color:#fff;font-size:12px;display:inline-flex;align-items:center;justify-content:center;margin-right:10px}

                @media (max-width:576px){.uca-search,.uca-search.sm{min-width:100%}.uca-actions,.uca-filters{width:100%}.uca-filter-select{flex:1;min-width:0}}
            `}</style>
        </React.Fragment>
    );
};

export default UserCouponAllocation;